import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { usePlatformAdmin } from '../../../hooks';
import { SUPER_REGION_OPTIONS } from '../../../constants/regions';
import { InfrastructureEditor } from '../../../components/profiles/InfrastructureEditor';
import { CapacityScheduleEditor } from '../../../components/profiles/CapacityScheduleEditor';
import type { InfrastructureFormData } from '../../../types/profile.types';
import type { CapacitySchedule } from '@interlucent/organization-protocol';
import { GenerateWorkerTokenDialog } from '../../../components/profiles/GenerateWorkerTokenDialog';
import { storePreviewContext } from '../../preview.$';
import { PoolAppMappingTree } from '../../../components/profiles/PoolAppMappingTree';
import { PoolMembershipEditor } from '../../../components/profiles/PoolMembershipEditor';
import { PredictiveObservability } from '../../../components/PredictiveObservability';
import styles from './PoolDetail.module.css';

export const Route = createFileRoute('/admin/pools/$poolId')({
  component: PoolDetailRoute,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  const wsUrl = (import.meta as any).env.VITE_API_URL || 'wss://api.dev.interlucent.ai';
  return wsUrl.replace(/^wss:/, 'https:').replace(/^ws:/, 'http:');
}

function poolUrl(poolId: string, path: string, pool?: AdminPool | null): string {
  const orgId = pool && pool.type !== 'by_request' ? pool.orgId : 'global';
  return `${getApiBaseUrl()}/pool/${orgId}:${poolId}${path}`;
}

function regionLabel(code: string): string {
  return SUPER_REGION_OPTIONS.find(r => r.value === code)?.label ?? code;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function relativeMs(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function formatDuration(ms: number | null): string {
  if (ms === null) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdminPool {
  id: string;
  name: string;
  type: string;
  region: string;
  settingsJson: string | null;
  capacityScheduleJson: string | null;
  active: boolean;
  createdAt: string;
  projectId: string;
  projectName: string;
  orgId: string;
  orgName: string;
}

interface PoolCounts {
  total: number;
  available: number;
  busy: number;
  reconciling: number;
  draining: number;
  pinned: number;
  totalSessionSlots: number;
  usedSessionSlots: number;
  availableSessionSlots: number;
}

interface PoolOverride {
  autoscalingPaused: boolean;
  pausedAt?: number;
  pauseReason?: string;
  scaling?: {
    desiredCapacity: number;
    minSize: number;
    maxSize: number;
    createdAt: number;
  };
}

interface ScalingDecisionRecord {
  id: number;
  at: number;
  strategyName: string;
  action: 'scale-up' | 'scale-down' | 'hold';
  currentSessionCapacity: number;
  targetSessionCapacity: number;
  reason: string;
  confidence: number;
  totalWorkers: number;
  availableWorkers: number;
  busyWorkers: number;
  totalSessionSlots: number;
  usedSessionSlots: number;
  demandUnmet: number | null;
  demandAvgWaitMs: number | null;
  activeScheduleWindow: string | null;
  executed: boolean;
}

interface ScaleActionRecord {
  at: number;
  direction: 'up' | 'down';
  fromInstanceCount: number;
  toInstanceCount: number;
  reason: string;
}

interface DemandSnapshot {
  fetchedAt: number;
  totalUnmet: number;
  avgWaitMs: number;
  matchRate: number;
  cells: Array<{ cellId: string; unmet: number }>;
  /** Populated when the row was recorded from a push-based unmet-demand signal. */
  appId?: string;
  versionId?: string;
}

interface ScaleUpLatencyRecord {
  id: number;
  requestedAt: number;
  fromWorkers: number;
  toWorkers: number;
  timeToFirstWorkerMs: number | null;
  timeToTargetMs: number | null;
  status: string;
  reason: string;
}

interface EnrichedInstance {
  instanceId: string;
  backendId: string;
  status: string;
  orchestratorId: string | null;
  publicIp?: string;
}

interface BoostEntry {
  id: number;
  addedSlots: number;
  createdAt: number;
  expiresAt: number;
  reason: string;
  createdBy: string;
}

interface DampenEntry {
  id: number;
  removedSlots: number;
  createdAt: number;
  expiresAt: number;
  reason: string;
  createdBy: string;
}

interface PoolStats {
  ok: boolean;
  poolId: string;
  counts: PoolCounts;
  override: PoolOverride;
  boosts?: BoostEntry[];
  dampens?: DampenEntry[];
  maxConcurrentByBackend: Record<string, number>;
  scalingDecisions: ScalingDecisionRecord[];
  scaleActions: ScaleActionRecord[];
  demandHistory: DemandSnapshot[];
  scaleUpLatency: ScaleUpLatencyRecord[];
}

interface OrchestratorInfo {
  workerId: string;
  instanceId: string;
  cloud: string;
  region: string;
  zone?: string;
  state: 'registered' | 'available' | 'busy' | 'reconciling' | 'draining';
  provisionedApps: Array<{ appId: string; versionId: string; ready: boolean }>;
  activeJobs: Array<{ jobId: string; appId: string; sessionId: string; startedAt: number }>;
  registeredAt: string;
  lastPingAt: string;
  pinned: boolean;
  managed: boolean;
  agentVersion?: string;
  os?: string;
  arch?: string;
  activeSessions: number;
  maxConcurrentSessions: number;
}

interface ProvisioningBackend {
  backendId: string;
  cloud: string;
  status: string;
  error?: string;
}

interface ProvisioningState {
  status: string;
  backends?: ProvisioningBackend[];
}

interface InfraScaling {
  currentCapacity: number;
  desiredCapacity: number;
  minSize: number;
  maxSize: number;
  isStable: boolean;
  instances?: Array<{ instanceId: string; lifecycleState: string }>;
}

interface InfraHealthBackend {
  backendId: string;
  cloud: string;
  region: string;
  healthy: boolean;
  currentCapacity: number;
  desiredCapacity: number;
  lastError?: string;
  lastCheckedAt: number;
}

interface InfraHealth {
  healthy: boolean;
  backends: InfraHealthBackend[];
}

interface InfraState {
  scaling: InfraScaling | null;
  health: InfraHealth | null;
  instances: EnrichedInstance[];
}

const DEFAULT_INFRA: InfrastructureFormData = {
  backends: [],
  allocationStrategy: 'balanced',
};

const REFRESH_INTERVAL_MS = 10_000;

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

function PoolDetailRoute() {
  const { poolId } = Route.useParams();
  const { isPlatformAdmin } = usePlatformAdmin();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  // Auto-pick the editor per pool state, replacing the old
  // ?membershipEditor=1 flag.
  //
  //   Pool has ≥1 inclusion or refinement → new PoolMembershipEditor.
  //     The pool is on the new model; the new editor is authoritative.
  //   Pool has 0 inclusions AND 0 refinements → legacy PoolAppMappingTree.
  //     The pool is still on the DPA/DPAV path; legacy editor is correct
  //     for it. A banner invites the operator to migrate.
  //
  // Edits in the legacy editor while the pool is in the "new" state are
  // misleading — the push path is driven by materializePool and would
  // overwrite the DPA/DPAV-derived config on the next ambient trigger.
  // Auto-picking avoids that footgun. Set to 'loading' on mount; flips
  // to 'new' | 'legacy' after /membership resolves.
  const [editorState, setEditorState] = useState<'loading' | 'new' | 'legacy'>('loading');

  // Data state
  const [pool, setPool] = useState<AdminPool | null>(null);
  const poolRef = useRef<AdminPool | null>(null);
  const [stats, setStats] = useState<PoolStats | null>(null);
  const [workers, setWorkers] = useState<OrchestratorInfo[]>([]);
  const [provState, setProvState] = useState<ProvisioningState | null>(null);
  const [infraState, setInfraState] = useState<InfraState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Effective tenancy: settingsJson.tenancy overrides the type-based default
  const effectiveTenancy = useMemo((): 'single' | 'organization' | 'multi' => {
    if (!pool) return 'single';
    try {
      const s = JSON.parse(pool.settingsJson ?? '{}');
      if (s.tenancy === 'multi') return 'multi';
      if (s.tenancy === 'organization') return 'organization';
      if (s.tenancy === 'single') return 'single';
    } catch { /* ignore */ }
    return pool.type === 'by_request' ? 'multi' : 'single';
  }, [pool]);
  const isMultiTenant = effectiveTenancy !== 'single';

  // Scale form
  const [scaleDesired, setScaleDesired] = useState('');
  const [scaleMin, setScaleMin] = useState('');
  const [scaleMax, setScaleMax] = useState('');
  const scaleFormInitialized = useRef(false);

  // Boost / Dampen form — magnitude, duration preset, reason. These are
  // expiring interventions on top of the scaling engine (see PoolDO.ts).
  const [boostSlots, setBoostSlots] = useState('');
  const [boostDuration, setBoostDuration] = useState('60'); // minutes
  const [boostReason, setBoostReason] = useState('');
  const [dampenSlots, setDampenSlots] = useState('');
  const [dampenDuration, setDampenDuration] = useState('60');
  const [dampenReason, setDampenReason] = useState('');

  // Action state
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Predictive observability state (IL-137). Populated by fetchPredictiveData
  // when the pool has predictive (or shadow) enabled.
  type PredictionRow = {
    timestampMs: number;
    forecastValue: number;
    contributed: number;
    actualDemand: number;
    coldStartStage: number;
    nEff: number;
    mode: string;
    region: string | null;
    reason: string | null;
  };
  type BinRow = { tBin: number; nEff: number; weeksObserved: number; lastUpdatedAt: number };
  type QuarantineRow = { id: number; tBin: number; observation: number; reason: string; atMs: number };
  const [predictions, setPredictions] = useState<PredictionRow[]>([]);
  const [histogramBins, setHistogramBins] = useState<BinRow[]>([]);
  const [predictiveTimezoneServed, setPredictiveTimezoneServed] = useState<string>("UTC");
  const [quarantine, setQuarantine] = useState<QuarantineRow[]>([]);

  // Provision panel state
  const [provisionResult, setProvisionResult] = useState<{ ok: boolean; message: string } | null>(null);

  // Debug panel state
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugData, setDebugData] = useState<unknown>(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [desiredData, setDesiredData] = useState<unknown>(null);
  const [desiredLoading, setDesiredLoading] = useState(false);
  // DPA/DPAV → pool_inclusions/pool_refinements migration (platform-wide op,
  // surfaced on the per-pool page so operators can inspect this pool's row
  // in the report without scrolling a large platform-wide JSON blob).
  const [migrateData, setMigrateData] = useState<unknown>(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateMode, setMigrateMode] = useState<"dry" | "execute" | null>(null);
  // Per-(app,version) JobBoardDO depth (the new pull-based marketplace).
  // Tells you which apps have submits queued on the new board and how many
  // worker pulls are currently parked. Empty when the JOB_BOARD_DO binding
  // isn't configured (legacy-only environment).
  const [boardDepthData, setBoardDepthData] = useState<unknown>(null);
  const [boardDepthLoading, setBoardDepthLoading] = useState(false);
  // Per-(app,version) AppVersionHistoryDO summary (last 5 minutes).
  // Aggregated counters that JobBoardDO ships every 30s. Useful for
  // confirming traffic is flowing through the new path without inspecting
  // an orc directly. Empty when APP_VERSION_HISTORY_DO isn't bound.
  const [boardHistoryData, setBoardHistoryData] = useState<unknown>(null);
  const [boardHistoryLoading, setBoardHistoryLoading] = useState(false);
  // JobBoardMux internal state (the new pull-based mux). markets list,
  // active loops, hold count, geographic anchor. Use when verifying that
  // PoolDO's setMarkets actually reached the mux for multiplex apps.
  const [boardMuxDebugData, setBoardMuxDebugData] = useState<unknown>(null);
  const [boardMuxDebugLoading, setBoardMuxDebugLoading] = useState(false);
  // Admin recovery: re-run materialize and force-push. Fixes pools whose
  // pool_materialized_entries went stale (e.g. the D1 bind-cap bug wiped
  // them) without waiting for an ambient trigger.
  const [rematerializeData, setRematerializeData] = useState<unknown>(null);
  const [rematerializeLoading, setRematerializeLoading] = useState(false);
  // Admin recovery: re-broadcast cached desired-state with a bumped
  // revision. Cheaper than rematerialize+push (no resolver round-trip);
  // used when only orc-side state has drifted (workers in RECONCILING
  // with APPS=0 but active heartbeats).
  const [refreshDesiredStateData, setRefreshDesiredStateData] = useState<unknown>(null);
  const [refreshDesiredStateLoading, setRefreshDesiredStateLoading] = useState(false);

  // Edit panel state
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [infraDefaults, setInfraDefaults] = useState<unknown>(null);

  // App mappings state
  const [poolApps, setPoolApps] = useState<Array<{ mappingId: string; appId: string; appName: string; appSlug: string; projectId: string; versionId: string | null; preLaunch: boolean; versions: Array<{ versionId: string; storageUrl: string | null; gameEngine: string | null; operatingSystem: string | null; isLocal: boolean | null; createdAt: string | null }> }>>([]);
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const [projectApps, setProjectApps] = useState<Array<{ id: string; name: string; appSlug: string }>>([]);
  const [selectedAppToAdd, setSelectedAppToAdd] = useState('');
  const [appActionInProgress, setAppActionInProgress] = useState<string | null>(null);
  // Multi-tenant app tree picker state
  type AppTreeOrg = { id: string; name: string; projects: Array<{ id: string; name: string; apps: Array<{ id: string; name: string; appSlug: string }> }> };
  const [appTree, setAppTree] = useState<AppTreeOrg[]>([]);
  const [appTreeLoading, setAppTreeLoading] = useState(false);
  const [appTreeExpandedOrgs, setAppTreeExpandedOrgs] = useState<Set<string>>(new Set());
  const [appTreeExpandedProjects, setAppTreeExpandedProjects] = useState<Set<string>>(new Set());
  const [appSearchSelectedId, setAppSearchSelectedId] = useState('');
  const [appTreeFilter, setAppTreeFilter] = useState('');

  // Token generation dialog
  const [showTokenDialog, setShowTokenDialog] = useState(false);

  // Activity log tabs
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activeLogTab, setActiveLogTab] = useState<'decisions' | 'actions' | 'latency' | 'demand'>('decisions');

  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (!isPlatformAdmin) {
      navigate({ to: '/' });
    }
  }, [isPlatformAdmin, navigate]);

  const authHeaders = useCallback(async () => {
    const token = await getToken({ template: 'default' });
    if (!token) throw new Error('Authentication required');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, [getToken]);

  // Detect which editor to show by probing the new membership endpoint.
  //
  //   - Pool with ≥1 inclusion or refinement → new editor (already migrated).
  //   - Pool with tenancy=multi → new editor regardless of row count.
  //     The legacy tree can't express multi-tenant catchment (it assumes a
  //     single project's app list); and the migrator intentionally writes
  //     zero rows for multi pools because there's no sensible auto-synth.
  //     Operators need to hand-curate inclusions, which only the new editor
  //     supports. Falling back to legacy here would silently hide the only
  //     editable UI from them.
  //   - Anything else → legacy editor with a migrate CTA.
  //
  // Failures (e.g. a pre-migration-0009 environment) conservatively fall
  // back to legacy so operators always see _something_ editable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/membership`, { headers });
        if (!res.ok) {
          if (!cancelled) setEditorState('legacy');
          return;
        }
        const data = await res.json() as {
          inclusions?: unknown[];
          refinements?: unknown[];
          pool?: { tenancy?: 'single' | 'organization' | 'multi' };
        };
        const hasRows = (data.inclusions?.length ?? 0) > 0 || (data.refinements?.length ?? 0) > 0;
        const isMulti = data.pool?.tenancy === 'multi';
        if (!cancelled) setEditorState(hasRows || isMulti ? 'new' : 'legacy');
      } catch {
        if (!cancelled) setEditorState('legacy');
      }
    })();
    return () => { cancelled = true; };
  }, [poolId, authHeaders]);

  // ---- Data fetching ----

  const fetchPool = useCallback(async () => {
    const headers = await authHeaders();
    const res = await fetch(`${getApiBaseUrl()}/api/admin/pools`, { headers });
    if (!res.ok) throw new Error(`Failed to fetch pools: HTTP ${res.status}`);
    const data = await res.json() as { pools: AdminPool[] };
    const found = data.pools.find(p => p.id === poolId);
    if (found) {
      setPool(found);
      poolRef.current = found;
    }
  }, [poolId, authHeaders]);

  // Predictive observability fetch — only fires when predictive or shadow is
  // enabled. Triple parallel fetch into local state. Failures fall through
  // silently; the surface degrades to "no data" without breaking the page.
  const fetchPredictiveData = useCallback(async () => {
    const headers = await authHeaders();
    try {
      const [predRes, binsRes, quarRes] = await Promise.all([
        fetch(poolUrl(poolId, '/predictive/predictions?limit=200', poolRef.current), { headers }),
        fetch(poolUrl(poolId, '/predictive/bins', poolRef.current), { headers }),
        fetch(poolUrl(poolId, '/predictive/quarantine?limit=50', poolRef.current), { headers }),
      ]);
      if (predRes.ok) {
        const data = await predRes.json() as { predictions: PredictionRow[] };
        setPredictions(data.predictions ?? []);
      }
      if (binsRes.ok) {
        const data = await binsRes.json() as { bins: BinRow[]; timezone: string };
        setHistogramBins(data.bins ?? []);
        setPredictiveTimezoneServed(data.timezone ?? 'UTC');
      }
      if (quarRes.ok) {
        const data = await quarRes.json() as { quarantine: QuarantineRow[] };
        setQuarantine(data.quarantine ?? []);
      }
    } catch {
      /* surface stays as last-known data; auto-refresh will retry */
    }
  }, [poolId, authHeaders]);

  const fetchRuntimeData = useCallback(async () => {
    const headers = await authHeaders();

    const [statsRes, workersRes, provRes, infraRes] = await Promise.all([
      fetch(poolUrl(poolId, '/stats', poolRef.current), { headers }),
      fetch(poolUrl(poolId, '/orchestrators', poolRef.current), { headers }),
      fetch(poolUrl(poolId, '/provisioning-state', poolRef.current), { headers }),
      fetch(poolUrl(poolId, '/infrastructure', poolRef.current), { headers }),
    ]);

    let statsData: PoolStats | null = null;
    let infraData: { scaling: InfraScaling | null; health: InfraHealth | null; instances: EnrichedInstance[] } | null = null;

    if (statsRes.ok) {
      statsData = await statsRes.json() as PoolStats;
      setStats(statsData);
    }

    if (workersRes.ok) {
      const data = await workersRes.json() as { orchestrators: OrchestratorInfo[] };
      setWorkers(data.orchestrators ?? []);
    }

    if (provRes.ok) {
      const data = await provRes.json() as { state: ProvisioningState };
      setProvState(data.state);
    }

    if (infraRes.ok) {
      infraData = await infraRes.json() as { scaling: InfraScaling | null; health: InfraHealth | null; instances: EnrichedInstance[] };
      setInfraState({ scaling: infraData.scaling, health: infraData.health, instances: infraData.instances ?? [] });
    }

    // Initialize scale form once from best available source
    if (!scaleFormInitialized.current) {
      scaleFormInitialized.current = true;
      if (statsData?.override?.scaling) {
        // Active override takes priority
        setScaleDesired(String(statsData.override.scaling.desiredCapacity));
        setScaleMin(String(statsData.override.scaling.minSize));
        setScaleMax(String(statsData.override.scaling.maxSize));
      } else {
        // Use infra scaling state (actual cloud values), fall back to session slots
        setScaleDesired(String(infraData?.scaling?.desiredCapacity ?? statsData?.counts?.totalSessionSlots ?? 0));
        setScaleMin(String(infraData?.scaling?.minSize ?? 0));
        setScaleMax(String(infraData?.scaling?.maxSize ?? 10));
      }
    }
  }, [poolId, authHeaders]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await fetchPool();
      await fetchRuntimeData();
      await fetchPredictiveData();
    } catch (err: any) {
      setError(err.message || 'Failed to load pool data');
    } finally {
      setLoading(false);
    }
  }, [fetchPool, fetchRuntimeData, fetchPredictiveData]);

  // Initial load + auto-refresh
  useEffect(() => {
    loadAll();

    refreshTimerRef.current = setInterval(() => {
      fetchRuntimeData().catch(() => {});
      fetchPredictiveData().catch(() => {});
    }, REFRESH_INTERVAL_MS);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadAll, fetchRuntimeData, fetchPredictiveData]);

  // ---- Actions ----

  const handleScale = useCallback(async () => {
    const desired = parseInt(scaleDesired, 10);
    if (isNaN(desired) || desired < 0) return;

    setActionInProgress('scale');
    try {
      const headers = await authHeaders();
      const body: Record<string, number> = { desiredCapacity: desired };
      const min = parseInt(scaleMin, 10);
      const max = parseInt(scaleMax, 10);
      if (!isNaN(min)) body.minSize = min;
      if (!isNaN(max)) body.maxSize = max;

      const res = await fetch(poolUrl(poolId, '/scale', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Scale request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, scaleDesired, scaleMin, scaleMax, authHeaders, fetchRuntimeData]);

  const handleAddBoost = useCallback(async () => {
    const slots = parseInt(boostSlots, 10);
    const duration = parseInt(boostDuration, 10);
    if (isNaN(slots) || slots <= 0) return;
    if (isNaN(duration) || duration <= 0) return;
    if (duration > 24 * 60) {
      setError('Boost duration cannot exceed 24h. For longer commitments, add a schedule span.');
      return;
    }

    setActionInProgress('boost');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/boost', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ addedSlots: slots, durationMinutes: duration, reason: boostReason.trim() || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      // Reset form magnitude and reason but keep duration (common operator workflow)
      setBoostSlots('');
      setBoostReason('');
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Boost request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, boostSlots, boostDuration, boostReason, authHeaders, fetchRuntimeData]);

  const handleRemoveBoost = useCallback(async (boostId: number) => {
    setActionInProgress(`remove-boost-${boostId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/boost/${boostId}`, poolRef.current), {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel boost');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleAddDampen = useCallback(async () => {
    const slots = parseInt(dampenSlots, 10);
    const duration = parseInt(dampenDuration, 10);
    if (isNaN(slots) || slots <= 0) return;
    if (isNaN(duration) || duration <= 0) return;
    if (duration > 24 * 60) {
      setError('Dampen duration cannot exceed 24h. For a permanent ceiling, set the pool max clamp.');
      return;
    }

    setActionInProgress('dampen');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/dampen', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ removedSlots: slots, durationMinutes: duration, reason: dampenReason.trim() || undefined }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setDampenSlots('');
      setDampenReason('');
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Dampen request failed');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, dampenSlots, dampenDuration, dampenReason, authHeaders, fetchRuntimeData]);

  const handleRemoveDampen = useCallback(async (dampenId: number) => {
    setActionInProgress(`remove-dampen-${dampenId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/dampen/${dampenId}`, poolRef.current), {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel dampen');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handlePauseAutoscaling = useCallback(async () => {
    setActionInProgress('pause');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/override', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ autoscalingPaused: true, pauseReason: 'admin console' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to pause autoscaling');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleResumeAutoscaling = useCallback(async () => {
    setActionInProgress('resume');
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, '/override', poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ action: 'clear' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to resume autoscaling');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleDrainOrchestrator = useCallback(async (orchestratorId: string) => {
    setActionInProgress(`drain-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/drain`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'manual' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to drain orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleTerminateOrchestrator = useCallback(async (orchestratorId: string) => {
    if (!confirm(`Terminate ${orchestratorId}? This will force-disconnect the orchestrator.`)) return;
    setActionInProgress(`terminate-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/terminate`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'evicted' }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRebootInstance = useCallback(async (instanceId: string) => {
    if (!confirm(`Reboot instance ${instanceId}? The OS will restart and the orchestrator will reconnect.`)) return;
    setActionInProgress(`reboot-${instanceId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/instances/${instanceId}/reboot`, poolRef.current), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to reboot instance');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleTerminateInstance = useCallback(async (instanceId: string) => {
    if (!confirm(`Terminate instance ${instanceId}? The pool autoscaler will provision a replacement to keep desired capacity.`)) return;
    setActionInProgress(`terminate-${instanceId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/instances/${instanceId}/terminate`, poolRef.current), {
        method: 'POST',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to terminate instance');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handlePinOrchestrator = useCallback(async (orchestratorId: string, pinned: boolean) => {
    setActionInProgress(`pin-${orchestratorId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(poolUrl(poolId, `/orchestrators/${orchestratorId}/pin`, poolRef.current), {
        method: 'POST',
        headers,
        body: JSON.stringify({ pinned }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to pin/unpin orchestrator');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  const handleRotateScripts = useCallback(async () => {
    if (!confirm('Rotate startup scripts on all provisioned backends? Only new instances will get the updated scripts.')) return;
    setActionInProgress('rotate-scripts');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/rotate-scripts`, {
        method: 'POST',
        headers,
      });
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await fetchRuntimeData();
    } catch (err: any) {
      setError(err.message || 'Failed to rotate scripts');
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  // ---- Provision handler ----

  const handleReprovision = useCallback(async (reset: boolean) => {
    const msg = reset
      ? 'Reset Infrastructure?\n\nAll instances will be terminated, manual overrides cleared, and infrastructure rebuilt from defaults.'
      : 'Refresh Infrastructure?\n\nAll instances will be terminated and infrastructure rebuilt. Manual scale overrides will be re-applied.';
    if (!window.confirm(msg)) return;
    setActionInProgress('provision');
    setProvisionResult(null);
    try {
      const headers = await authHeaders();
      const url = `${getApiBaseUrl()}/api/admin/pools/${poolId}/provision${reset ? '?reset=true' : ''}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setProvisionResult({
        ok: true,
        message: data.ok ? `${reset ? 'Reset' : 'Refresh'} completed successfully` : `Result: ${JSON.stringify(data)}`,
      });
      fetchRuntimeData();
    } catch (err: any) {
      setProvisionResult({ ok: false, message: err.message || 'Failed to reprovision' });
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  // ---- Per-backend reprovision handler ----

  const handleReprovisionBackend = useCallback(async (backendId: string, label: string) => {
    if (!window.confirm(`Reprovision backend ${label}?\n\nInstances on this backend will be terminated and the backend rebuilt. Other backends are not affected.`)) return;
    setActionInProgress(`reprovision-${backendId}`);
    setProvisionResult(null);
    try {
      const headers = await authHeaders();
      const url = `${getApiBaseUrl()}/api/admin/pools/${poolId}/provision/${encodeURIComponent(backendId)}`;
      const res = await fetch(url, { method: 'POST', headers });
      const data = await res.json() as any;
      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setProvisionResult({
        ok: true,
        message: `Backend ${label} reprovisioned successfully`,
      });
      fetchRuntimeData();
    } catch (err: any) {
      setProvisionResult({ ok: false, message: `Backend ${label}: ${err.message || 'Failed to reprovision'}` });
    } finally {
      setActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchRuntimeData]);

  // ---- Debug handler ----

  const handleFetchDebug = useCallback(async (opts?: { includeDownloadUrls?: boolean; bypassCache?: boolean }) => {
    setDebugLoading(true);
    setDebugData(null);
    try {
      const headers = await authHeaders();
      const qs = new URLSearchParams();
      if (opts?.includeDownloadUrls) qs.set('downloadUrls', '1');
      if (opts?.bypassCache) qs.set('bypassCache', '1');
      const query = qs.toString() ? `?${qs.toString()}` : '';
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/debug${query}`, { headers });
      const data = await res.json();
      setDebugData(data);
    } catch (err: any) {
      setDebugData({ error: err.message || 'Failed to fetch debug info' });
    } finally {
      setDebugLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardDepth = useCallback(async () => {
    setBoardDepthLoading(true);
    setBoardDepthData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/board-depth`, { headers });
      const data = await res.json();
      setBoardDepthData(data);
    } catch (err: any) {
      setBoardDepthData({ error: err.message || 'Failed to fetch board depth' });
    } finally {
      setBoardDepthLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardHistory = useCallback(async () => {
    setBoardHistoryLoading(true);
    setBoardHistoryData(null);
    try {
      const headers = await authHeaders();
      // Default since = now - 5min (matches the backend default).
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/board-history`, { headers });
      const data = await res.json();
      setBoardHistoryData(data);
    } catch (err: any) {
      setBoardHistoryData({ error: err.message || 'Failed to fetch board history' });
    } finally {
      setBoardHistoryLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleFetchBoardMuxDebug = useCallback(async () => {
    setBoardMuxDebugLoading(true);
    setBoardMuxDebugData(null);
    try {
      const headers = await authHeaders();
      // Pool admin needs the orgId to build the mux DO name. by_request
      // pools route through the global mux; everyone else uses the org's.
      if (!pool) {
        setBoardMuxDebugData({ error: "pool not loaded yet — refresh the page" });
        return;
      }
      const orgId = pool.type === 'by_request' ? 'global' : pool.orgId;
      const res = await fetch(
        `${getApiBaseUrl()}/pool/${orgId}/${poolId}/board-mux/debug`,
        { headers },
      );
      const json = await res.json().catch(() => ({}));
      setBoardMuxDebugData(json);
    } catch (err: any) {
      setBoardMuxDebugData({ error: err.message || 'Failed to fetch board mux debug' });
    } finally {
      setBoardMuxDebugLoading(false);
    }
  }, [pool, poolId, authHeaders]);

  const handleFetchDesiredState = useCallback(async () => {
    setDesiredLoading(true);
    setDesiredData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/desired-state`, { headers });
      const data = await res.json();
      setDesiredData(data);
    } catch (err: any) {
      setDesiredData({ error: err.message || 'Failed to fetch desired state' });
    } finally {
      setDesiredLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleRematerialize = useCallback(async () => {
    setRematerializeLoading(true);
    setRematerializeData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/rematerialize`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      setRematerializeData(data);
    } catch (err: any) {
      setRematerializeData({ error: err.message || 'Failed to re-materialize' });
    } finally {
      setRematerializeLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleRefreshDesiredState = useCallback(async () => {
    setRefreshDesiredStateLoading(true);
    setRefreshDesiredStateData(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/refresh-desired-state`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      setRefreshDesiredStateData(data);
    } catch (err: any) {
      setRefreshDesiredStateData({ error: err.message || 'Failed to refresh desired state' });
    } finally {
      setRefreshDesiredStateLoading(false);
    }
  }, [poolId, authHeaders]);

  const handleMigrateMappings = useCallback(async (mode: "dry" | "execute", scope: "all" | "this" = "all") => {
    if (mode === "execute") {
      const msg = scope === "all"
        ? ("Run pool-mapping migration WITH WRITES across EVERY non-deleted pool?\n\n" +
           "Translates DPA/DPAV into pool_inclusions + pool_refinements. Idempotent\n" +
           "(skips already-migrated pools) but irreversible without a D1 restore.\n\nProceed?")
        : ("Migrate THIS pool from DPA/DPAV to pool_inclusions + pool_refinements?\n\n" +
           "Writes rows into the new tables, leaves DPA/DPAV intact until the\n" +
           "platform-wide cut-over. Idempotent (no-op if already migrated) but\n" +
           "irreversible without a D1 restore.\n\nProceed?");
      if (!window.confirm(msg)) return;
    }
    setMigrateLoading(true);
    setMigrateMode(mode);
    setMigrateData(null);
    try {
      const headers = await authHeaders();
      const qs = mode === "dry" ? "?dryRun=true" : "?dryRun=false";
      const path = scope === "all"
        ? `/api/admin/pools/migrate-mappings${qs}`
        : `/api/admin/pools/${poolId}/migrate-mappings${qs}`;
      const res = await fetch(`${getApiBaseUrl()}${path}`, {
        method: "POST",
        headers,
      });
      const data = await res.json();
      setMigrateData(data);
    } catch (err: any) {
      setMigrateData({ error: err.message || "Failed to run migration" });
    } finally {
      setMigrateLoading(false);
    }
  }, [authHeaders, poolId]);

  // ---- Fetch infra defaults for edit form ----

  const fetchInfraDefaults = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pool-infra-defaults`, { headers });
      if (res.ok) {
        const data = await res.json();
        setInfraDefaults((data as any).defaults);
      }
    } catch { /* non-critical */ }
  }, [authHeaders]);

  useEffect(() => {
    if (editOpen && infraDefaults === null) {
      fetchInfraDefaults();
    }
  }, [editOpen, infraDefaults, fetchInfraDefaults]);

  // ---- App Mappings ----

  const fetchPoolApps = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/apps`, { headers });
      if (res.ok) {
        const data = await res.json() as { apps: typeof poolApps };
        setPoolApps(data.apps);
      }
    } catch { /* non-critical */ }
  }, [poolId, authHeaders]);

  const fetchProjectApps = useCallback(async () => {
    if (!pool?.projectId) return;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/projects/${pool.projectId}/apps`, { headers });
      if (res.ok) {
        const data = await res.json() as { apps: typeof projectApps };
        setProjectApps(data.apps);
      }
    } catch { /* non-critical */ }
  }, [pool?.projectId, authHeaders]);

  // Fetch the full org→project→app tree (for multi-tenant pools)
  const fetchAppTree = useCallback(async () => {
    setAppTreeLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/apps/tree`, { headers });
      if (res.ok) {
        const data = await res.json() as { tree: typeof appTree };
        setAppTree(data.tree);
        // Auto-expand if only one org
        if (data.tree.length === 1) {
          setAppTreeExpandedOrgs(new Set([data.tree[0].id]));
        }
      }
    } catch { /* non-critical */ } finally {
      setAppTreeLoading(false);
    }
  }, [authHeaders]);

  // Load app mappings once pool data is available
  useEffect(() => {
    if (pool) {
      fetchPoolApps();
      if (isMultiTenant) {
        fetchAppTree();
      } else {
        fetchProjectApps();
      }
    }
  }, [pool, isMultiTenant, fetchPoolApps, fetchProjectApps, fetchAppTree]);

  const handleAddApp = useCallback(async () => {
    const appId = isMultiTenant ? appSearchSelectedId : selectedAppToAdd;
    if (!appId) return;
    setAppActionInProgress('add');
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/apps`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ appId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setSelectedAppToAdd('');
      setAppSearchSelectedId('');
      await fetchPoolApps();
    } catch (err: any) {
      setError(err.message || 'Failed to add app');
    } finally {
      setAppActionInProgress(null);
    }
  }, [poolId, pool?.type, selectedAppToAdd, appSearchSelectedId, authHeaders, fetchPoolApps]);

  const handleRemoveApp = useCallback(async (appId: string) => {
    setAppActionInProgress(`remove-${appId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/apps/${appId}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchPoolApps();
    } catch (err: any) {
      setError(err.message || 'Failed to remove app');
    } finally {
      setAppActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchPoolApps]);

  const handleUpdateAppMapping = useCallback(async (appId: string, updates: { versionId?: string | null; preLaunch?: boolean }) => {
    setAppActionInProgress(`update-${appId}`);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/apps/${appId}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      await fetchPoolApps();
    } catch (err: any) {
      setError(err.message || 'Failed to update app mapping');
    } finally {
      setAppActionInProgress(null);
    }
  }, [poolId, authHeaders, fetchPoolApps]);

  const handlePreview = useCallback(async (appId: string, appName: string, versionId: string, projectId: string) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/preview-share`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId, versionId }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      const data = await res.json() as { id: string };
      storePreviewContext({
        title: appName,
        projectId,
        appId,
        versionId,
      });
      window.open(`/preview/${data.id}`, '_blank');
    } catch (err: any) {
      setError(err.message || 'Failed to create preview');
    }
  }, [poolId, authHeaders]);

  // ---- Edit / Update handler ----

  const handleUpdatePool = useCallback(async (settingsJson: string, capacityScheduleJson: string | null, name: string, region: string, active: boolean) => {
    setEditSaving(true);
    setEditMessage(null);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          region,
          settingsJson: settingsJson || undefined,
          capacityScheduleJson: capacityScheduleJson || undefined,
          active,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as any).error || `HTTP ${res.status}`);
      }
      setEditMessage({ ok: true, text: 'Pool updated successfully' });
      // Refresh pool data
      await fetchPool();
    } catch (err: any) {
      setEditMessage({ ok: false, text: err.message || 'Failed to update pool' });
    } finally {
      setEditSaving(false);
    }
  }, [poolId, authHeaders, fetchPool]);

  // ---- Worker token generation (REST-based for admin page) ----

  const generateWorkerToken = useCallback(async (_profileId: string, expiresInSeconds?: number) => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${getApiBaseUrl()}/api/admin/pools/${poolId}/worker-token`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ expiresInSeconds }),
      });
      const data = await res.json() as any;
      if (!res.ok) return { success: false, error: data.error || `HTTP ${res.status}` };
      return { success: true, token: data.token, expiresInSeconds: data.expiresInSeconds };
    } catch (err: any) {
      return { success: false, error: err?.message || 'Failed to generate token' };
    }
  }, [poolId, authHeaders]);

  if (!isPlatformAdmin) return null;

  if (loading && !stats) {
    return <div className={styles.loading}>Loading pool details...</div>;
  }

  const counts = stats?.counts;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backLink} onClick={() => navigate({ to: '/admin/pools' })}>
            &larr; Pools
          </button>
          <h1 className={styles.poolTitle}>{pool?.name ?? poolId}</h1>
        </div>
        <div className={styles.headerActions}>
          <button
            className={styles.actionButton}
            onClick={() => setShowTokenDialog(true)}
          >
            Generate Token
          </button>
          <div className={styles.headerBadges}>
            {pool && (
              <span className={pool.active ? styles.badgeActive : styles.badgeInactive}>
                {pool.active ? 'Active' : 'Inactive'}
              </span>
            )}
            {pool && <span className={styles.badgeRegion}>{regionLabel(pool.region)}</span>}
          </div>
        </div>
      </header>

      {/* Pool Identity */}
      {pool && (
        <div className={styles.poolIdentity}>
          <span className={styles.identityItem}>
            <span className={styles.identityLabel}>Pool:</span>
            <span className={styles.identityValue} onClick={() => navigator.clipboard.writeText(pool.id)} title="Click to copy">{pool.id}</span>
          </span>
          <span className={styles.identityItem}>
            <span className={styles.identityLabel}>Org:</span>
            <span className={styles.identityValue} onClick={() => navigator.clipboard.writeText(pool.orgId)} title="Click to copy">{pool.orgName} ({pool.orgId})</span>
          </span>
          <span className={styles.identityItem}>
            <span className={styles.identityLabel}>Project:</span>
            <span className={styles.identityValue} onClick={() => navigator.clipboard.writeText(pool.projectId)} title="Click to copy">{pool.projectName} ({pool.projectId})</span>
          </span>
          <span className={styles.identityItem}>
            <span className={styles.identityLabel}>Type:</span>
            <span className={styles.identityValue}>{pool.type}</span>
          </span>
          <span className={styles.identityItem}>
            <span className={styles.identityLabel}>Created:</span>
            <span className={styles.identityValue}>{new Date(pool.createdAt).toLocaleDateString()}</span>
          </span>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Capacity Pipeline */}
      <div className={styles.pipelineCard}>
        <h3 className={styles.pipelineCardTitle}>Capacity Pipeline</h3>
        <div className={styles.pipelineGrid}>
          {/* Infrastructure Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Infrastructure</div>
            <div className={styles.pipelineColumnSubtitle}>Machine Instances</div>
            {infraState?.scaling ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Machines Running</span>
                  <span className={styles.statValue}>{infraState.scaling.currentCapacity}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Machines Desired</span>
                  <span className={styles.statValue}>{infraState.scaling.desiredCapacity}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Min / Max</span>
                  <span className={styles.statValue}>{infraState.scaling.minSize} / {infraState.scaling.maxSize}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Stable</span>
                  <span className={styles.statValue}>{infraState.scaling.isStable ? 'Yes' : 'No'}</span>
                </div>
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No cloud infrastructure</div>
            )}
            {/* Per-backend breakdown */}
            {infraState?.health?.backends && infraState.health.backends.length > 0 && (
              <div className={styles.backendList}>
                {infraState.health.backends.map(b => {
                  const provBackend = provState?.backends?.find(pb => pb.backendId === b.backendId);
                  const label = `${b.cloud}/${b.region}`;
                  return (
                    <div key={b.backendId} className={styles.backendRow}>
                      <span className={styles.backendId}>{label}</span>
                      <span className={styles.statValue}>{b.currentCapacity}/{b.desiredCapacity}</span>
                      <span className={b.healthy ? styles.provProvisioned : styles.provFailed}>
                        {provBackend?.status === 'failed' ? 'FAIL' : b.healthy ? 'OK' : 'ERR'}
                      </span>
                      <button
                        className={styles.actionButton}
                        style={{ padding: '0 var(--space-2)', fontSize: 'var(--text-xs)', lineHeight: 1.6 }}
                        disabled={!!actionInProgress}
                        onClick={() => handleReprovisionBackend(b.backendId, label)}
                      >
                        {actionInProgress === `reprovision-${b.backendId}` ? '...' : 'Reprovision'}
                      </button>
                    </div>
                  );
                })}
                {infraState.health.backends.filter(b => b.lastError).map(b => (
                  <div key={`${b.backendId}-err`} className={styles.backendError}>{b.backendId}: {b.lastError}</div>
                ))}
              </div>
            )}
            {/* Failed backends not in health (failed provisioning) */}
            {provState?.backends?.filter(pb =>
              pb.status === 'failed' &&
              !infraState?.health?.backends?.some(hb => hb.backendId === pb.backendId)
            ).map(pb => (
              <div key={pb.backendId} className={styles.backendRow} style={{ marginTop: 'var(--space-1)' }}>
                <span className={styles.backendId}>{pb.cloud}/{pb.backendId}</span>
                <span className={styles.provFailed}>FAIL</span>
                <button
                  className={styles.actionButton}
                  style={{ padding: '0 var(--space-2)', fontSize: 'var(--text-xs)', lineHeight: 1.6 }}
                  disabled={!!actionInProgress}
                  onClick={() => handleReprovisionBackend(pb.backendId, `${pb.cloud}/${pb.backendId}`)}
                >
                  {actionInProgress === `reprovision-${pb.backendId}` ? '...' : 'Retry'}
                </button>
                {pb.error && <div className={styles.backendError}>{pb.error}</div>}
              </div>
            ))}
            {/* Pending machines */}
            {(() => {
              const pendingMachines = Math.max(0, (infraState?.scaling?.currentCapacity ?? 0) - (counts?.total ?? 0));
              if (pendingMachines > 0) return (
                <div style={{ marginTop: 'var(--space-2)' }}>
                  <span className={styles.pendingBadge}>Pending: {pendingMachines} machine{pendingMachines !== 1 ? 's' : ''}</span>
                  <div className={styles.pendingNote}>Launched, not yet registered</div>
                </div>
              );
              return null;
            })()}
          </div>

          {/* Orchestrators Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Orchestrators</div>
            <div className={styles.pipelineColumnSubtitle}>Connected Agents</div>
            {counts ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Total</span>
                  <span className={styles.statValue}>{counts.total}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Available</span>
                  <span className={styles.statValue}>{counts.available}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Busy</span>
                  <span className={styles.statValue}>{counts.busy}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Reconciling</span>
                  <span className={styles.statValue}>{counts.reconciling}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Draining</span>
                  <span className={styles.statValue}>{counts.draining}</span>
                </div>
                {counts.pinned > 0 && (
                  <div className={styles.statRow}>
                    <span className={styles.statLabel}>Pinned</span>
                    <span className={styles.statValue}>{counts.pinned}</span>
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No data</div>
            )}
          </div>

          {/* Session Slots Column */}
          <div className={styles.pipelineColumn}>
            <div className={styles.pipelineColumnTitle}>Session Slots</div>
            <div className={styles.pipelineColumnSubtitle}>Demand & Utilization</div>
            {counts ? (
              <>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Used</span>
                  <span className={styles.statValue}>{counts.usedSessionSlots} / {counts.totalSessionSlots}</span>
                </div>
                <div className={styles.statRow}>
                  <span className={styles.statLabel}>Available</span>
                  <span className={styles.statValue}>{counts.availableSessionSlots}</span>
                </div>
                {(() => {
                  const desiredSessions = infraState?.scaling?.desiredSessionCapacity ?? infraState?.scaling?.desiredCapacity ?? 0;
                  const pendingSessions = Math.max(0, desiredSessions - counts.totalSessionSlots);
                  const effectiveSlots = counts.totalSessionSlots + pendingSessions;
                  if (pendingSessions > 0) return (
                    <div className={styles.statRow}>
                      <span className={styles.statLabel}>Effective</span>
                      <span className={styles.statValue} style={{ color: 'var(--text-tertiary)' }}>
                        {effectiveSlots} ({counts.totalSessionSlots} actual + {pendingSessions} pend.)
                      </span>
                    </div>
                  );
                  return null;
                })()}
              </>
            ) : (
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>No data</div>
            )}
          </div>
        </div>

        {/* Provisioning footer */}
        <div className={styles.pipelineFooter}>
          <div className={styles.statRow}>
            <span className={styles.statLabel}>Provisioning</span>
            {provState ? (
              <span className={provisioningBadgeClass(provState.status)}>
                {provState.status.replace(/_/g, ' ')}
              </span>
            ) : (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>unknown</span>
            )}
          </div>
        </div>
      </div>

      {/* Machines & Orchestrators (unified) */}
      <InstancesAndOrchestrators
        instances={infraState?.instances ?? []}
        orchestrators={workers}
        actionInProgress={actionInProgress}
        onDrain={handleDrainOrchestrator}
        onTerminate={handleTerminateOrchestrator}
        onPin={handlePinOrchestrator}
        onReboot={handleRebootInstance}
        onTerminateInstance={handleTerminateInstance}
        onRefresh={() => fetchRuntimeData().catch(() => {})}
        onRefreshDesiredState={handleRefreshDesiredState}
        refreshDesiredStateLoading={refreshDesiredStateLoading}
        refreshDesiredStateData={refreshDesiredStateData}
      />

      {/* Scaling Controls */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Scaling Controls</h2>
        </div>
        <div className={styles.controlsGrid}>
          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Boost (Extra Capacity)</div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.6, margin: '0 0 var(--space-2) 0' }}>
              Add session slots <em>on top of</em> whatever autoscaling already wants, for a limited time. If the engine wants 10 and you boost +20, the pool gets 30. Multiple active boosts stack. Still clamped by the pool's max and by any active dampen. Expires automatically.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>+ Slots</span>
              <input
                className={styles.inputField}
                type="number"
                min="1"
                value={boostSlots}
                onChange={e => setBoostSlots(e.target.value)}
                placeholder="e.g. 10"
              />
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Duration</span>
              <select
                className={styles.inputField}
                value={boostDuration}
                onChange={e => setBoostDuration(e.target.value)}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="240">4 hours</option>
                <option value="480">8 hours</option>
                <option value="1440">24 hours (max)</option>
              </select>
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Reason</span>
              <input
                className={styles.inputField}
                type="text"
                value={boostReason}
                onChange={e => setBoostReason(e.target.value)}
                placeholder="e.g. marketing launch"
                maxLength={500}
              />
            </div>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={handleAddBoost}
                disabled={actionInProgress === 'boost' || !boostSlots}
              >
                {actionInProgress === 'boost' ? 'Boosting...' : 'Boost'}
              </button>
            </div>
            {stats?.boosts && stats.boosts.length > 0 && (
              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Active Boosts
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  {stats.boosts.map(b => {
                    const minsLeft = Math.max(0, Math.ceil((b.expiresAt - Date.now()) / 60000));
                    return (
                      <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', background: 'var(--bg-secondary, rgba(0,0,0,0.03))' }}>
                        <span>
                          <strong>+{b.addedSlots}</strong> — {b.reason} <span style={{ opacity: 0.6 }}>by {b.createdBy}, {minsLeft}m left</span>
                        </span>
                        <button
                          onClick={() => handleRemoveBoost(b.id)}
                          disabled={actionInProgress === `remove-boost-${b.id}`}
                          style={{ padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--text-xs)', background: 'transparent', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}
                        >
                          {actionInProgress === `remove-boost-${b.id}` ? '...' : 'Cancel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Dampen (Temporary Ceiling)</div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.6, margin: '0 0 var(--space-2) 0' }}>
              Cap session slots for a limited time. Ceiling = current − removed. Use for cost emergencies or load-test throttling. Expires automatically.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>− Slots</span>
              <input
                className={styles.inputField}
                type="number"
                min="1"
                value={dampenSlots}
                onChange={e => setDampenSlots(e.target.value)}
                placeholder="e.g. 5"
              />
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Duration</span>
              <select
                className={styles.inputField}
                value={dampenDuration}
                onChange={e => setDampenDuration(e.target.value)}
              >
                <option value="15">15 minutes</option>
                <option value="30">30 minutes</option>
                <option value="60">1 hour</option>
                <option value="240">4 hours</option>
                <option value="480">8 hours</option>
                <option value="1440">24 hours (max)</option>
              </select>
            </div>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Reason</span>
              <input
                className={styles.inputField}
                type="text"
                value={dampenReason}
                onChange={e => setDampenReason(e.target.value)}
                placeholder="e.g. cost emergency"
                maxLength={500}
              />
            </div>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={handleAddDampen}
                disabled={actionInProgress === 'dampen' || !dampenSlots}
              >
                {actionInProgress === 'dampen' ? 'Dampening...' : 'Dampen'}
              </button>
            </div>
            {stats?.dampens && stats.dampens.length > 0 && (
              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border)' }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Active Dampens
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
                  {stats.dampens.map(d => {
                    const minsLeft = Math.max(0, Math.ceil((d.expiresAt - Date.now()) / 60000));
                    return (
                      <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-2)', background: 'var(--bg-secondary, rgba(0,0,0,0.03))' }}>
                        <span>
                          <strong>−{d.removedSlots}</strong> — {d.reason} <span style={{ opacity: 0.6 }}>by {d.createdBy}, {minsLeft}m left</span>
                        </span>
                        <button
                          onClick={() => handleRemoveDampen(d.id)}
                          disabled={actionInProgress === `remove-dampen-${d.id}`}
                          style={{ padding: 'var(--space-1) var(--space-2)', fontSize: 'var(--text-xs)', background: 'transparent', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}
                        >
                          {actionInProgress === `remove-dampen-${d.id}` ? '...' : 'Cancel'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Manual Scale (Session Slots)</div>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning, #b45309)', margin: '0 0 var(--space-2) 0' }}>
              ⚠ Prefer <strong>Boost</strong> for temporary capacity increases. Manual scale disables autoscaling entirely and does not expire.
            </p>
            <div className={styles.inputRow}>
              <span className={styles.inputLabel}>Desired</span>
              <input
                className={styles.inputField}
                type="number"
                min="0"
                value={scaleDesired}
                onChange={e => setScaleDesired(e.target.value)}
                placeholder="0"
              />
            </div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.5, margin: 'var(--space-1) 0' }}>
              Machines = ceil(desired / backend maxConcurrent). Pauses autoscaling.
            </p>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={handleScale}
                disabled={actionInProgress === 'scale' || !scaleDesired}
              >
                {actionInProgress === 'scale' ? 'Scaling...' : 'Apply'}
              </button>
            </div>
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Autoscaling</div>
            <div className={styles.overrideStatus}>
              <span className={styles.overrideLabel}>Status:</span>
              <span className={`${styles.overrideValue} ${stats?.override?.autoscalingPaused ? styles.overrideActive : styles.overrideInactive}`}>
                {stats?.override?.autoscalingPaused ? 'Paused' : 'Active'}
              </span>
            </div>
            {stats?.override?.autoscalingPaused && stats.override.pauseReason && (
              <div className={styles.overrideStatus}>
                <span className={styles.overrideLabel}>Reason:</span>
                <span className={styles.overrideValue}>{stats.override.pauseReason}</span>
              </div>
            )}
            {stats?.override?.scaling?.desiredCapacity != null && (
              <div className={styles.overrideStatus}>
                <span className={styles.overrideLabel}>Target:</span>
                <span className={styles.overrideValue}>{stats.override.scaling.desiredCapacity} session slots</span>
              </div>
            )}
            <div className={styles.controlActions}>
              {stats?.override?.autoscalingPaused ? (
                <button
                  className={styles.applyButton}
                  onClick={handleResumeAutoscaling}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'resume' ? 'Resuming...' : 'Resume'}
                </button>
              ) : (
                <button
                  className={styles.actionButton}
                  onClick={handlePauseAutoscaling}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'pause' ? 'Pausing...' : 'Pause'}
                </button>
              )}
              {(stats?.override?.scaling || stats?.override?.autoscalingPaused) && (
                <button
                  className={styles.dangerButton}
                  onClick={handleResumeAutoscaling}
                  disabled={!!actionInProgress}
                >
                  Clear All Overrides
                </button>
              )}
            </div>
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Infrastructure</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              <div>
                <p className={styles.overrideStatus} style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Update launch templates with latest startup scripts. Only new instances are affected.
                </p>
                <button
                  className={styles.actionButton}
                  onClick={handleRotateScripts}
                  disabled={!!actionInProgress}
                >
                  {actionInProgress === 'rotate-scripts' ? 'Rotating...' : 'Rotate Scripts'}
                </button>
              </div>
              <div style={{ borderTop: 'var(--border-width) solid var(--border)', paddingTop: 'var(--space-3)' }}>
                <p className={styles.overrideStatus} style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginBottom: 'var(--space-2)' }}>
                  Teardown and rebuild infrastructure. Terminates all running instances.
                </p>
                <div className={styles.controlActions}>
                  <button
                    className={styles.applyButton}
                    onClick={() => handleReprovision(false)}
                    disabled={!!actionInProgress}
                  >
                    {actionInProgress === 'provision' ? 'Working...' : 'Refresh Infrastructure'}
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => handleReprovision(true)}
                    disabled={!!actionInProgress}
                    style={{ borderColor: 'var(--error)', color: 'var(--error)' }}
                  >
                    Reset Infrastructure
                  </button>
                </div>
                <p style={{ fontSize: 'var(--text-xs)', opacity: 0.5, marginTop: 'var(--space-1)' }}>
                  Refresh preserves manual scale overrides. Reset clears all overrides to defaults.
                </p>
              </div>
            </div>
            {provisionResult && (
              <div style={{
                marginTop: 'var(--space-2)',
                padding: 'var(--space-2) var(--space-3)',
                fontSize: 'var(--text-xs)',
                border: 'var(--border-width) solid',
                borderColor: provisionResult.ok ? 'var(--text-primary)' : 'var(--color-error)',
                color: provisionResult.ok ? 'var(--text-primary)' : 'var(--color-error)',
              }}>
                {provisionResult.message}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* App Mapping — auto-picked per pool state.
       *
       * The new PoolMembershipEditor renders for pools with any
       * pool_inclusions or pool_refinements rows (the explicit-opt-in
       * model). The legacy PoolAppMappingTree renders for pools that
       * haven't been migrated yet (still on the DPA/DPAV override
       * chain). A banner on the legacy path surfaces the migrate-this-
       * pool button in the Debug section. */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {editorState === 'new' ? 'App Mappings (membership)' : 'App Mappings'}
          </h2>
        </div>
        {editorState === 'loading' && (
          <div style={{ padding: 'var(--space-4)', color: 'var(--text-tertiary)', fontSize: 'var(--text-xs)' }}>
            Detecting pool membership model…
          </div>
        )}
        {editorState === 'legacy' && (
          <div style={{
            marginBottom: 'var(--space-3)',
            padding: 'var(--space-3)',
            border: 'var(--border-width) solid var(--border)',
            background: 'var(--bg-secondary)',
            fontSize: 'var(--text-xs)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'var(--space-3)',
          }}>
            <span>
              This pool is on the <strong>legacy</strong> mapping model (DPA/DPAV). Edits here
              take effect today, but will be superseded once the pool is migrated to the new
              explicit-opt-in model. Open the <strong>Debug</strong> section below and click{' '}
              <strong>Migrate This Pool</strong> to switch.
            </span>
            <button
              type="button"
              className={styles.actionButton}
              onClick={() => {
                setDebugOpen(true);
                // Small delay to let the panel render, then scroll to it.
                setTimeout(() => {
                  const el = document.querySelector(`.${styles.section}:last-of-type`);
                  el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
              }}
            >
              Jump to Migrate
            </button>
          </div>
        )}
        {editorState === 'new' && (
          <PoolMembershipEditor
            poolId={poolId}
            authHeaders={authHeaders}
            apiBaseUrl={getApiBaseUrl()}
            onError={(msg) => setError(msg)}
          />
        )}
        {editorState === 'legacy' && (
          <PoolAppMappingTree
            poolId={poolId}
            authHeaders={authHeaders}
            apiBaseUrl={getApiBaseUrl()}
            onError={(msg) => setError(msg)}
          />
        )}
      </div>


      {/* Scaling Status Panel */}
      {stats?.scalingDecisions && stats.scalingDecisions.length > 0 && (() => {
        const latest = stats.scalingDecisions[0];
        const lastAction = stats.scaleActions?.[0];
        const labelStyle: React.CSSProperties = { fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)' as any, textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', color: 'var(--text-tertiary)' };
        const valueStyle: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--text-primary)', marginTop: 2 };
        return (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Scaling Status</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-4)', padding: 'var(--space-3) 0' }}>
              <div>
                <div style={labelStyle}>Last Evaluation</div>
                <div style={valueStyle}>{relativeMs(latest.at)}</div>
                <div style={{ ...valueStyle, fontWeight: 'var(--weight-semibold)' as any }}>
                  <span className={
                    latest.action === 'scale-up' ? styles.provProvisioned :
                    latest.action === 'scale-down' ? styles.provFailed :
                    styles.provPending
                  }>{latest.action}</span>
                  {' '}{latest.currentSessionCapacity} → {latest.targetSessionCapacity} slots
                </div>
              </div>
              <div>
                <div style={labelStyle}>Reason</div>
                <div style={{ ...valueStyle, fontSize: 'var(--text-xs)', wordBreak: 'break-word' }}>{latest.reason}</div>
              </div>
              <div>
                <div style={labelStyle}>Last Executed Action</div>
                {lastAction ? (
                  <div style={valueStyle}>
                    {lastAction.direction === 'up' ? 'Scale-up' : 'Scale-down'} {relativeMs(lastAction.at)}
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)' }}>
                      {lastAction.fromInstanceCount} → {lastAction.toInstanceCount} slots — {lastAction.reason}
                    </div>
                  </div>
                ) : (
                  <div style={{ ...valueStyle, color: 'var(--text-tertiary)' }}>None</div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Activity Log (tabbed, collapsible) */}
      {(stats?.scalingDecisions?.length || stats?.scaleActions?.length || stats?.scaleUpLatency?.length || stats?.demandHistory?.length) ? (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Activity Log</h2>
            <button
              className={styles.actionButton}
              onClick={() => setActivityLogOpen(prev => !prev)}
            >
              {activityLogOpen ? 'Collapse' : 'Expand'}
            </button>
          </div>
          {activityLogOpen && <><div className={styles.tabBar}>
            <button
              className={activeLogTab === 'decisions' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveLogTab('decisions')}
            >
              Decisions{stats?.scalingDecisions?.length ? ` (${stats.scalingDecisions.length})` : ''}
            </button>
            <button
              className={activeLogTab === 'actions' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveLogTab('actions')}
            >
              Actions{stats?.scaleActions?.length ? ` (${stats.scaleActions.length})` : ''}
            </button>
            <button
              className={activeLogTab === 'latency' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveLogTab('latency')}
            >
              Latency{stats?.scaleUpLatency?.length ? ` (${stats.scaleUpLatency.length})` : ''}
            </button>
            <button
              className={activeLogTab === 'demand' ? styles.tabButtonActive : styles.tabButton}
              onClick={() => setActiveLogTab('demand')}
            >
              Demand{stats?.demandHistory?.length ? ` (${stats.demandHistory.length})` : ''}
            </button>
          </div>

          {/* Decisions Tab */}
          {activeLogTab === 'decisions' && stats?.scalingDecisions && stats.scalingDecisions.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Reason</th>
                  <th>Current</th>
                  <th>Target</th>
                  <th>Slots (used/total)</th>
                  <th>Unmet</th>
                  <th>Executed</th>
                </tr>
              </thead>
              <tbody>
                {stats.scalingDecisions.slice(0, 25).map(d => (
                  <tr key={d.id}>
                    <td title={new Date(d.at).toISOString()}>{relativeMs(d.at)}</td>
                    <td>
                      <span className={
                        d.action === 'scale-up' ? styles.provProvisioned :
                        d.action === 'scale-down' ? styles.provFailed :
                        styles.provPending
                      }>
                        {d.action}
                      </span>
                    </td>
                    <td style={{ fontSize: 'var(--text-xs)', maxWidth: 400, wordBreak: 'break-word' }} title={d.reason}>
                      {d.reason}
                      {d.activeScheduleWindow && (
                        <div style={{ color: 'var(--text-tertiary)', marginTop: 2 }}>
                          Schedule: {d.activeScheduleWindow}
                        </div>
                      )}
                    </td>
                    <td>{d.currentSessionCapacity}</td>
                    <td>{d.targetSessionCapacity}</td>
                    <td>{d.usedSessionSlots}/{d.totalSessionSlots}</td>
                    <td>{d.demandUnmet ?? '-'}</td>
                    <td>{d.executed ? 'Y' : 'N'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Actions Tab */}
          {activeLogTab === 'actions' && stats?.scaleActions && stats.scaleActions.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Direction</th>
                  <th>From (machines)</th>
                  <th>To (machines)</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {stats.scaleActions.slice(0, 15).map((a, i) => (
                  <tr key={i}>
                    <td title={new Date(a.at).toISOString()}>{relativeMs(a.at)}</td>
                    <td>
                      <span className={a.direction === 'up' ? styles.provProvisioned : styles.provFailed}>
                        {a.direction}
                      </span>
                    </td>
                    <td>{a.fromInstanceCount}</td>
                    <td>{a.toInstanceCount}</td>
                    <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Latency Tab */}
          {activeLogTab === 'latency' && stats?.scaleUpLatency && stats.scaleUpLatency.length > 0 && (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Requested</th>
                  <th>From (machines)</th>
                  <th>To (machines)</th>
                  <th>Time to First</th>
                  <th>Time to Target</th>
                  <th>Status</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {stats.scaleUpLatency.map(l => (
                  <tr key={l.id}>
                    <td title={new Date(l.requestedAt).toISOString()}>{relativeMs(l.requestedAt)}</td>
                    <td>{l.fromWorkers}</td>
                    <td>{l.toWorkers}</td>
                    <td>{formatDuration(l.timeToFirstWorkerMs)}</td>
                    <td>{formatDuration(l.timeToTargetMs)}</td>
                    <td>
                      <span className={provisioningBadgeClass(
                        l.status === 'completed' ? 'provisioned' :
                        l.status === 'failed' || l.status === 'timed_out' ? 'failed' : 'pending'
                      )}>
                        {l.status}
                      </span>
                    </td>
                    <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Demand Tab — push-based unmet-demand signals grouped by app:version.
              Each row is one incoming push from a marketplace that couldn't
              place a job against direct workers or mux virtual agents. */}
          {activeLogTab === 'demand' && stats?.demandHistory && stats.demandHistory.length > 0 && (() => {
            // Group by app:version (falling back to "unattributed" for legacy
            // polled rows that pre-date the push refactor).
            const grouped = new Map<string, DemandSnapshot[]>();
            for (const d of stats.demandHistory) {
              const key = d.appId && d.versionId ? `${d.appId}:${d.versionId}` : 'unattributed';
              const arr = grouped.get(key) ?? [];
              arr.push(d);
              grouped.set(key, arr);
            }
            // Sort groups by most-recent push descending so active
            // app:versions float to the top.
            const orderedGroups = [...grouped.entries()]
              .map(([key, rows]) => {
                const latest = Math.max(...rows.map((r) => r.fetchedAt));
                return [key, rows, latest] as const;
              })
              .sort((a, b) => b[2] - a[2]);

            // Top-level rollup: fleet-wide total across all app:version groups.
            const fleetTotal = stats.demandHistory.length;
            const fleetPeakUnmet = stats.demandHistory.reduce(
              (m, d) => Math.max(m, d.totalUnmet),
              0,
            );
            const fleetFirst = Math.min(...stats.demandHistory.map((d) => d.fetchedAt));
            const fleetLatest = Math.max(...stats.demandHistory.map((d) => d.fetchedAt));

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)',
                  padding: 'var(--space-2) var(--space-3)',
                  background: 'var(--color-bg-secondary)',
                  border: 'var(--border-width) solid var(--color-border)',
                }}>
                  <span style={{
                    fontWeight: 'var(--weight-semibold)' as any,
                    color: 'var(--color-text-primary)',
                    marginRight: 'var(--space-3)',
                  }}>
                    {fleetTotal} push{fleetTotal === 1 ? '' : 'es'} across {orderedGroups.length} app:version{orderedGroups.length === 1 ? '' : 's'}
                  </span>
                  peak unmet {fleetPeakUnmet} · span {relativeMs(fleetFirst)} → {relativeMs(fleetLatest)}
                </div>

                {orderedGroups.map(([key, rows]) => {
                  const sorted = [...rows].sort((a, b) => b.fetchedAt - a.fetchedAt);
                  const totalPushes = rows.length;
                  const latestJobCount = sorted[0]?.totalUnmet ?? 0;
                  const peakUnmet = rows.reduce((m, r) => Math.max(m, r.totalUnmet), 0);
                  const firstAt = Math.min(...rows.map((r) => r.fetchedAt));
                  const latestAt = Math.max(...rows.map((r) => r.fetchedAt));
                  return (
                    <div key={key}>
                      <div style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--text-xs)',
                        letterSpacing: 'var(--tracking-wide)',
                        textTransform: 'uppercase',
                        color: 'var(--color-text-tertiary)',
                        marginBottom: 'var(--space-2)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 'var(--space-3)',
                        alignItems: 'baseline',
                      }}>
                        <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--weight-semibold)' as any }}>{key}</span>
                        <span>{totalPushes} push{totalPushes === 1 ? '' : 'es'}</span>
                        <span>latest {latestJobCount} unmet · {relativeMs(latestAt)}</span>
                        <span>peak {peakUnmet}</span>
                        <span>first {relativeMs(firstAt)}</span>
                      </div>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th style={{ width: '6rem' }}>Time</th>
                            <th style={{ width: '5rem' }}>Unmet</th>
                            <th style={{ width: '5rem' }}>Total</th>
                            <th>Relative</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sorted.map((d, i) => {
                            const totalMp =
                              Array.isArray(d.cells) && d.cells[0] && 'totalUnmatchedMarketplace' in d.cells[0]
                                ? (d.cells[0] as unknown as { totalUnmatchedMarketplace: number }).totalUnmatchedMarketplace
                                : null;
                            const barWidth = peakUnmet > 0 ? Math.max(2, Math.round((d.totalUnmet / peakUnmet) * 100)) : 0;
                            return (
                              <tr key={i}>
                                <td title={new Date(d.fetchedAt).toISOString()} style={{ fontFamily: 'var(--font-mono)' }}>
                                  {relativeMs(d.fetchedAt)}
                                </td>
                                <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' as const }}>{d.totalUnmet}</td>
                                <td style={{ fontFamily: 'var(--font-mono)', textAlign: 'right' as const, color: 'var(--color-text-tertiary)' }}>
                                  {totalMp ?? '-'}
                                </td>
                                <td>
                                  <div
                                    aria-hidden="true"
                                    style={{
                                      background: 'var(--color-accent)',
                                      opacity: 0.7,
                                      height: '10px',
                                      width: `${barWidth}%`,
                                      minWidth: barWidth > 0 ? '2px' : '0',
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Empty state for selected tab */}
          {activeLogTab === 'decisions' && (!stats?.scalingDecisions || stats.scalingDecisions.length === 0) && (
            <div className={styles.empty}>No scaling decisions recorded</div>
          )}
          {activeLogTab === 'actions' && (!stats?.scaleActions || stats.scaleActions.length === 0) && (
            <div className={styles.empty}>No scale actions recorded</div>
          )}
          {activeLogTab === 'latency' && (!stats?.scaleUpLatency || stats.scaleUpLatency.length === 0) && (
            <div className={styles.empty}>No scale-up latency data</div>
          )}
          {activeLogTab === 'demand' && (!stats?.demandHistory || stats.demandHistory.length === 0) && (
            <div className={styles.empty}>No demand history</div>
          )}
          </>}
        </div>
      ) : null}

      {/* Edit Pool (collapsible) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Edit Pool</h2>
          <button
            className={styles.actionButton}
            onClick={() => setEditOpen(prev => !prev)}
          >
            {editOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {editOpen && pool && (
          <EditPoolForm
            pool={pool}
            saving={editSaving}
            message={editMessage}
            infraDefaults={infraDefaults}
            onSave={handleUpdatePool}
            predictions={predictions}
            histogramBins={histogramBins}
            quarantine={quarantine}
            predictiveTimezoneServed={predictiveTimezoneServed}
          />
        )}
      </div>

      {/* Debug (collapsible) */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Debug</h2>
          <button
            className={styles.actionButton}
            onClick={() => setDebugOpen(prev => !prev)}
          >
            {debugOpen ? 'Collapse' : 'Expand'}
          </button>
        </div>
        {debugOpen && (
          <div>
            <div style={{ marginBottom: 'var(--space-3)', display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <button
                className={styles.actionButton}
                onClick={() => handleFetchDebug()}
                disabled={debugLoading}
              >
                {debugLoading ? 'Fetching...' : 'Fetch Debug'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleFetchDebug({ includeDownloadUrls: true })}
                disabled={debugLoading}
                title="Fetch Debug + a per-backend request-download-url preview. Uses the 23-min SQLite cache — subsequent fetches for the same (cloud,region,app) return the cached result."
              >
                {debugLoading ? 'Fetching...' : 'Fetch Debug + URL Preview'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleFetchDebug({ includeDownloadUrls: true, bypassCache: true })}
                disabled={debugLoading}
                title="Same as URL Preview but bypasses the 23-min download-url cache — forces fresh cache HEADs. Use when diagnosing stale R2 entries after hydration lands."
              >
                {debugLoading ? 'Fetching...' : 'URL Preview (fresh)'}
              </button>
              <button
                className={styles.actionButton}
                onClick={handleFetchDesiredState}
                disabled={desiredLoading}
              >
                {desiredLoading ? 'Fetching...' : 'Fetch Desired State'}
              </button>
              <button
                className={styles.actionButton}
                onClick={handleFetchBoardDepth}
                disabled={boardDepthLoading}
                title="Per-(app,version) JobBoardDO depth (the new pull-based marketplace). Open = jobs sitting on the board waiting for a worker pull; holding = worker pulls currently parked. Use to verify the dark-launch gate is routing submits to the new board for gated apps and that workers are pulling."
              >
                {boardDepthLoading ? 'Fetching...' : 'Fetch Board Depth'}
              </button>
              <button
                className={styles.actionButton}
                onClick={handleFetchBoardHistory}
                disabled={boardHistoryLoading}
                title="Per-(app,version) AppVersionHistoryDO summary (last 5 minutes by default). Aggregated counters JobBoardDO ships every 30s — posted, claimed, timeouts, expired plus high-water peaks. Use to see throughput on gated apps without poking the JobBoardDO directly."
              >
                {boardHistoryLoading ? 'Fetching...' : 'Fetch Board History'}
              </button>
              <button
                className={styles.actionButton}
                onClick={handleFetchBoardMuxDebug}
                disabled={boardMuxDebugLoading}
                title="JobBoardMux internal state for this pool: configured markets (set by PoolDO via setMarkets), active pull loops, current hold count, geographic anchor. Use to verify multiplex apps are reaching the mux. Empty `markets` while you have multiplex apps means PoolDO's setMarkets didn't land."
              >
                {boardMuxDebugLoading ? 'Fetching...' : 'Fetch Board Mux Debug'}
              </button>
              <button
                className={styles.actionButton}
                onClick={handleRematerialize}
                disabled={rematerializeLoading}
                title="Re-run materializePool (rewrites pool_materialized_entries) and force-push the fresh config to PoolDO, bypassing the durable push queue. Use to recover pools whose materialized rows are stale or missing (e.g. coverage pills empty, launch gated as 'no_pool')."
                style={{ borderColor: 'var(--color-warning, #d97706)', color: 'var(--color-warning, #d97706)' }}
              >
                {rematerializeLoading ? 'Re-materializing…' : 'Re-materialize + Force Push'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleMigrateMappings("dry", "this")}
                disabled={migrateLoading}
                title="Dry-run this pool only: translate its DPA/DPAV to pool_inclusions + pool_refinements, report added/removed apps vs the legacy resolver. No writes."
              >
                {migrateLoading && migrateMode === "dry" ? 'Running dry-run…' : 'Migrate This Pool (dry-run)'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleMigrateMappings("execute", "this")}
                disabled={migrateLoading}
                title="Commit the migration for this pool only. Idempotent. Confirm before running."
                style={{ borderColor: 'var(--color-warning, #d97706)', color: 'var(--color-warning, #d97706)' }}
              >
                {migrateLoading && migrateMode === "execute" ? 'Executing…' : 'Migrate This Pool (EXECUTE)'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleMigrateMappings("dry", "all")}
                disabled={migrateLoading}
                title="Platform-wide dry-run: translate every non-deleted pool, report per-pool diffs. No writes."
              >
                {migrateLoading && migrateMode === "dry" ? 'Running dry-run…' : 'Migrate All (dry-run)'}
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleMigrateMappings("execute", "all")}
                disabled={migrateLoading}
                title="Platform-wide COMMIT for every un-migrated pool. Idempotent. Confirm before running."
                style={{ borderColor: 'var(--color-warning, #d97706)', color: 'var(--color-warning, #d97706)' }}
              >
                {migrateLoading && migrateMode === "execute" ? 'Executing…' : 'Migrate All (EXECUTE)'}
              </button>
            </div>
            {debugData !== null && (
              <>
                <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-2) 0' }}>PoolDO Internal State</h3>
                <pre style={{
                  padding: 'var(--space-3)',
                  border: 'var(--border-width) solid var(--border)',
                  background: 'var(--bg-secondary)',
                  fontSize: 'var(--text-xs)',
                  overflow: 'auto',
                  maxHeight: '500px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(debugData, null, 2)}
                </pre>
              </>
            )}
            {desiredData !== null && (
              <>
                <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>Desired State (OrgWsDO Config)</h3>
                <pre style={{
                  padding: 'var(--space-3)',
                  border: 'var(--border-width) solid var(--border)',
                  background: 'var(--bg-secondary)',
                  fontSize: 'var(--text-xs)',
                  overflow: 'auto',
                  maxHeight: '500px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {JSON.stringify(desiredData, null, 2)}
                </pre>
              </>
            )}
            {boardDepthData !== null && (() => {
              interface BoardDepth { open: number; holding: number; directs: number; muxes: number; }
              const d = boardDepthData as {
                depth?: Record<string, BoardDepth>;
                fetchedAt?: string;
                errors?: number;
                errorDetails?: Record<string, { status: number | null; reason?: string }>;
                note?: string;
                error?: string;
              } | null;
              const rows = d?.depth ? Object.entries(d.depth) : [];
              const hasErrors = (d?.errors ?? 0) > 0;
              return (
                <>
                  <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>
                    JobBoard Depth (new pull-based marketplace)
                  </h3>
                  {d?.note && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-warning, #d97706)' }}>
                      <strong>Note:</strong> {d.note}
                    </div>
                  )}
                  {d && !d.error && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
                      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-2)' }}>
                        <span><strong>Boards responding:</strong> {rows.length}</span>
                        {hasErrors && (
                          <span style={{ color: 'var(--color-error, #dc2626)' }}>
                            <strong>⚠ Errors:</strong> {d.errors}
                          </span>
                        )}
                        <span><strong>Fetched:</strong> {d.fetchedAt}</span>
                      </div>
                      {rows.length > 0 && (
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: 'var(--text-xs)',
                          marginBottom: 'var(--space-2)',
                        }}>
                          <thead>
                            <tr style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                              <th style={{ textAlign: 'left', padding: '4px 8px' }}>app:version</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Open jobs on the board (posted, not yet claimed)">Open</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Worker pulls currently parked (long-poll holds)">Holding</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Direct-worker pulls">Direct</th>
                              <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Mux pulls">Mux</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map(([key, b]) => {
                              const stuck = b.open > 0 && b.holding === 0;
                              return (
                                <tr key={key} style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                                  <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>
                                    {key}
                                    {stuck && (
                                      <span style={{ color: 'var(--color-error, #dc2626)', marginLeft: 'var(--space-2)' }}>
                                        ⚠ open jobs, no pulls held
                                      </span>
                                    )}
                                  </td>
                                  <td style={{ textAlign: 'right', padding: '4px 8px', color: b.open > 0 ? 'var(--color-warning, #d97706)' : undefined }}>{b.open}</td>
                                  <td style={{ textAlign: 'right', padding: '4px 8px', color: b.holding === 0 ? 'var(--color-error, #dc2626)' : undefined }}>{b.holding}</td>
                                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{b.directs}</td>
                                  <td style={{ textAlign: 'right', padding: '4px 8px' }}>{b.muxes}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                  <pre style={{
                    padding: 'var(--space-3)',
                    border: `var(--border-width) solid ${hasErrors ? 'var(--color-error, #dc2626)' : 'var(--border)'}`,
                    background: 'var(--bg-secondary)',
                    fontSize: 'var(--text-xs)',
                    overflow: 'auto',
                    maxHeight: '500px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: d?.error ? 'var(--color-error, #dc2626)' : undefined,
                  }}>
                    {JSON.stringify(boardDepthData, null, 2)}
                  </pre>
                </>
              );
            })()}
            {boardHistoryData !== null && (() => {
              interface History {
                since: number; until: number; windows: number;
                posted: number; claimed: number; timeouts: number; expired: number;
                peak_open: number; peak_holding: number; unclaimed_in_window: number;
              }
              const d = boardHistoryData as {
                history?: Record<string, History>;
                fetchedAt?: string;
                errors?: number;
                errorDetails?: Record<string, { status: number | null; reason?: string }>;
                note?: string;
                error?: string;
              } | null;
              const rows = d?.history ? Object.entries(d.history) : [];
              const hasErrors = (d?.errors ?? 0) > 0;
              return (
                <>
                  <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>
                    JobBoard History (last 5 min)
                  </h3>
                  {d?.note && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-warning, #d97706)' }}>
                      <strong>Note:</strong> {d.note}
                    </div>
                  )}
                  {d && !d.error && rows.length > 0 && (
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 'var(--text-xs)',
                      marginBottom: 'var(--space-2)',
                    }}>
                      <thead>
                        <tr style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                          <th style={{ textAlign: 'left',  padding: '4px 8px' }}>app:version</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Number of 30s flush windows aggregated">Wins</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Jobs posted to the board in window">Posted</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Claims completed in window">Claimed</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Pull-side timeouts (worker waited the full long-poll without a match)">T/O</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="Jobs that expired before being claimed (TTL hit)">Exp</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="High-water mark of open jobs in window">PkOpen</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="High-water mark of held pulls in window">PkHold</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }} title="posted - claimed: demand the system didn't absorb in window">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(([key, h]) => {
                          const stuck = h.unclaimed_in_window > 0;
                          return (
                            <tr key={key} style={{ borderBottom: 'var(--border-width) solid var(--border)' }}>
                              <td style={{ padding: '4px 8px', fontFamily: 'var(--font-mono)' }}>
                                {key}
                                {stuck && (
                                  <span style={{ color: 'var(--color-warning, #d97706)', marginLeft: 'var(--space-2)' }}>
                                    Δ unclaimed
                                  </span>
                                )}
                              </td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.windows}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.posted}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.claimed}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.timeouts}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px', color: h.expired > 0 ? 'var(--color-warning, #d97706)' : undefined }}>{h.expired}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.peak_open}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px' }}>{h.peak_holding}</td>
                              <td style={{ textAlign: 'right', padding: '4px 8px', color: stuck ? 'var(--color-warning, #d97706)' : undefined }}>{h.unclaimed_in_window}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <pre style={{
                    padding: 'var(--space-3)',
                    border: `var(--border-width) solid ${hasErrors ? 'var(--color-error, #dc2626)' : 'var(--border)'}`,
                    background: 'var(--bg-secondary)',
                    fontSize: 'var(--text-xs)',
                    overflow: 'auto',
                    maxHeight: '500px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: d?.error ? 'var(--color-error, #dc2626)' : undefined,
                  }}>
                    {JSON.stringify(boardHistoryData, null, 2)}
                  </pre>
                </>
              );
            })()}
            {boardMuxDebugData !== null && (() => {
              const d = boardMuxDebugData as {
                doName?: string;
                markets?: string[];
                activeLoops?: string[];
                center?: { lat: number; lng: number };
                holding?: number;
                error?: string;
              } | null;
              const noMarkets = !d?.error && (d?.markets?.length ?? 0) === 0;
              return (
                <>
                  <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>
                    JobBoard Mux Debug (new pull-based mux)
                  </h3>
                  {d && !d.error && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                      <span><strong>DO:</strong> {d.doName ?? '(unnamed)'}</span>
                      <span><strong>Markets:</strong> {d.markets?.length ?? 0}</span>
                      <span><strong>Active loops:</strong> {d.activeLoops?.length ?? 0}</span>
                      <span><strong>Holding:</strong> {d.holding ?? 0}</span>
                      <span><strong>Center:</strong> {d.center ? `${d.center.lat.toFixed(2)}, ${d.center.lng.toFixed(2)}` : '(none)'}</span>
                      {noMarkets && (
                        <span style={{ color: 'var(--color-warning, #d97706)' }}>
                          <strong>⚠ No markets configured</strong>
                          <span style={{ opacity: 0.8 }}>
                            {' '}— either no app in this pool is multiplex=true, or PoolDO's setMarkets hasn't reached this mux yet
                          </span>
                        </span>
                      )}
                    </div>
                  )}
                  <pre style={{
                    padding: 'var(--space-3)',
                    border: `var(--border-width) solid ${noMarkets ? 'var(--color-warning, #d97706)' : 'var(--border)'}`,
                    background: 'var(--bg-secondary)',
                    fontSize: 'var(--text-xs)',
                    overflow: 'auto',
                    maxHeight: '500px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: d?.error ? 'var(--color-error, #dc2626)' : undefined,
                  }}>
                    {JSON.stringify(boardMuxDebugData, null, 2)}
                  </pre>
                </>
              );
            })()}
            {rematerializeData !== null && (() => {
              const d = rematerializeData as {
                ok?: boolean;
                materializedCount?: number;
                materializedRevision?: number;
                pushed?: boolean;
                pushError?: string;
                error?: string;
              } | null;
              const ok = d?.ok === true;
              return (
                <>
                  <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>
                    Re-materialize + Force Push
                  </h3>
                  {d && !d.error && (
                    <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                      <span><strong>Materialized:</strong> {d.materializedCount ?? 0} entries</span>
                      <span><strong>Revision:</strong> {d.materializedRevision ?? 0}</span>
                      <span style={{ color: ok ? 'var(--color-success, #16a34a)' : 'var(--color-error, #dc2626)' }}>
                        <strong>{ok ? '✓ Pushed to PoolDO' : '✗ Push failed'}</strong>
                      </span>
                    </div>
                  )}
                  <pre style={{
                    padding: 'var(--space-3)',
                    border: `var(--border-width) solid ${ok ? 'var(--border)' : 'var(--color-error, #dc2626)'}`,
                    background: 'var(--bg-secondary)',
                    fontSize: 'var(--text-xs)',
                    overflow: 'auto',
                    maxHeight: '300px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: d?.error ? 'var(--color-error, #dc2626)' : undefined,
                  }}>
                    {JSON.stringify(rematerializeData, null, 2)}
                  </pre>
                </>
              );
            })()}
            {migrateData !== null && (
              <>
                <h3 style={{ fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', margin: 'var(--space-3) 0 var(--space-2)' }}>
                  Pool Mapping Migration — {migrateMode === "dry" ? "Dry Run" : "Execute"}
                </h3>
                {(() => {
                  const d = migrateData as {
                    dryRun?: boolean;
                    totalPools?: number;
                    migrated?: number;
                    skippedAlreadyMigrated?: number;
                    zeroDiffPools?: number;
                    nonzeroDiffPools?: number;
                    poolsRequiringReconfiguration?: number;
                    report?: Array<{ poolId: string; [k: string]: unknown }>;
                    // Single-pool response shape flattens the report entry at top level.
                    poolId?: string;
                    status?: string;
                    pool?: { id: string };
                    error?: string;
                  } | null;
                  if (!d) return null;
                  if (d.error) {
                    return (
                      <pre style={{
                        padding: 'var(--space-3)',
                        border: 'var(--border-width) solid var(--color-error, #dc2626)',
                        background: 'var(--bg-secondary)',
                        fontSize: 'var(--text-xs)',
                        overflow: 'auto',
                        maxHeight: '500px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: 'var(--color-error, #dc2626)',
                      }}>{JSON.stringify(d, null, 2)}</pre>
                    );
                  }
                  // Detect shape: platform-wide has `report[]`, single-pool has `status`.
                  const isSinglePool = Array.isArray(d.report) === false && typeof d.status === "string";
                  if (isSinglePool) {
                    return (
                      <div>
                        <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                          <span><strong>Scope:</strong> this pool</span>
                          <span><strong>Mode:</strong> {d.dryRun ? "dry-run (no writes)" : "EXECUTED (committed)"}</span>
                          <span><strong>Status:</strong> {d.status}</span>
                        </div>
                        <pre style={{
                          padding: 'var(--space-3)',
                          border: 'var(--border-width) solid var(--border)',
                          background: 'var(--bg-secondary)',
                          fontSize: 'var(--text-xs)',
                          overflow: 'auto',
                          maxHeight: '500px',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                        }}>{JSON.stringify(d, null, 2)}</pre>
                      </div>
                    );
                  }
                  const thisPoolReport = (d.report ?? []).find((r) => r.poolId === poolId);
                  const otherPools = (d.report ?? []).filter((r) => r.poolId !== poolId);
                  return (
                    <div>
                      <div style={{ marginBottom: 'var(--space-2)', fontSize: 'var(--text-xs)', display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                        <span><strong>Scope:</strong> platform-wide</span>
                        <span><strong>Mode:</strong> {d.dryRun ? "dry-run (no writes)" : "EXECUTED (committed)"}</span>
                        <span><strong>Pools:</strong> {d.totalPools}</span>
                        <span><strong>Migrated:</strong> {d.migrated}</span>
                        <span><strong>Skipped:</strong> {d.skippedAlreadyMigrated}</span>
                        <span><strong>0-diff:</strong> {d.zeroDiffPools}</span>
                        <span><strong>Non-0-diff:</strong> {d.nonzeroDiffPools}</span>
                        {(d.poolsRequiringReconfiguration ?? 0) > 0 && (
                          <span style={{ color: 'var(--color-warning, #d97706)' }}>
                            <strong>Needs reconfig:</strong> {d.poolsRequiringReconfiguration}
                          </span>
                        )}
                      </div>
                      {thisPoolReport && (
                        <>
                          <div style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-bold)', margin: 'var(--space-2) 0 var(--space-1)' }}>
                            This pool ({poolId})
                          </div>
                          <pre style={{
                            padding: 'var(--space-3)',
                            border: 'var(--border-width) solid var(--border)',
                            background: 'var(--bg-secondary)',
                            fontSize: 'var(--text-xs)',
                            overflow: 'auto',
                            maxHeight: '320px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}>{JSON.stringify(thisPoolReport, null, 2)}</pre>
                        </>
                      )}
                      {otherPools.length > 0 && (
                        <details style={{ marginTop: 'var(--space-2)' }}>
                          <summary style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-bold)', cursor: 'pointer' }}>
                            Other pools ({otherPools.length})
                          </summary>
                          <pre style={{
                            padding: 'var(--space-3)',
                            border: 'var(--border-width) solid var(--border)',
                            background: 'var(--bg-secondary)',
                            fontSize: 'var(--text-xs)',
                            overflow: 'auto',
                            maxHeight: '500px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}>{JSON.stringify(otherPools, null, 2)}</pre>
                        </details>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        )}
      </div>

      {/* Worker Token Dialog */}
      {showTokenDialog && pool && (
        <GenerateWorkerTokenDialog
          profileId={pool.id}
          profileName={pool.name}
          onClose={() => setShowTokenDialog(false)}
          generateWorkerToken={generateWorkerToken}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Edit Pool Form (inline)
// ---------------------------------------------------------------------------

function EditPoolForm({
  pool,
  saving,
  message,
  infraDefaults,
  onSave,
  predictions,
  histogramBins,
  quarantine,
  predictiveTimezoneServed,
}: {
  pool: AdminPool;
  saving: boolean;
  message: { ok: boolean; text: string } | null;
  infraDefaults: unknown;
  onSave: (settingsJson: string, capacityScheduleJson: string | null, name: string, region: string, active: boolean) => void;
  // Predictive observability props (IL-137). Owned by PoolDetailRoute, which
  // does the auto-refresh fetches; passed in as read-only data.
  predictions: Array<{
    timestampMs: number; forecastValue: number; contributed: number; actualDemand: number;
    coldStartStage: number; nEff: number; mode: string; region: string | null; reason: string | null;
  }>;
  histogramBins: Array<{ tBin: number; nEff: number; weeksObserved: number; lastUpdatedAt: number }>;
  quarantine: Array<{ id: number; tBin: number; observation: number; reason: string; atMs: number }>;
  predictiveTimezoneServed: string;
}) {
  const [name, setName] = useState(pool.name);
  const [region, setRegion] = useState(pool.region);
  const [active, setActive] = useState(pool.active);

  const existingSettings = useMemo(() => {
    if (!pool.settingsJson) return null;
    try { return JSON.parse(pool.settingsJson); } catch { return null; }
  }, [pool.settingsJson]);

  const [infrastructure, setInfrastructure] = useState<InfrastructureFormData>(() => {
    if (existingSettings) {
      const backends = (existingSettings.backends ?? []).map((b: any) => ({
        ...b,
        awsSubnets: Array.isArray(b.awsSubnets) ? b.awsSubnets.join(', ') : b.awsSubnets,
        awsSecurityGroups: Array.isArray(b.awsSecurityGroups) ? b.awsSecurityGroups.join(', ') : b.awsSecurityGroups,
        gcpFirewallTags: Array.isArray(b.gcpFirewallTags) ? b.gcpFirewallTags.join(', ') : b.gcpFirewallTags,
        // CoreWeave: convert server format back to form format
        cwRootPvcLabel: b.cwRootPvcLabel && typeof b.cwRootPvcLabel === 'object'
          ? Object.entries(b.cwRootPvcLabel).map(([k, v]) => `${k}=${v}`).join(', ')
          : b.cwRootPvcLabel,
        cwAdditionalDiskSize: Array.isArray(b.cwAdditionalDisks) && b.cwAdditionalDisks[0]?.size
          ? b.cwAdditionalDisks[0].size
          : b.cwAdditionalDiskSize,
      }));
      return {
        backends: backends.length ? backends : DEFAULT_INFRA.backends,
        allocationStrategy: existingSettings.allocationStrategy ?? 'balanced',
      };
    }
    return DEFAULT_INFRA;
  });

  const [tenancy, setTenancy] = useState<'single' | 'organization' | 'multi'>(
    existingSettings?.tenancy ?? (pool.type === 'by_request' ? 'multi' : 'single')
  );

  const [scalingMin, setScalingMin] = useState(String(existingSettings?.scaling?.min ?? 0));
  // 100 session slots ≈ 25 workers at typical maxConcurrent=4. Kept in sync
  // with the backend default in OrgWsDO; the old value of 10 silently
  // clamped pools at ~3 concurrent users once backfill's 3x headroom
  // kicked in. See F3 in the pool defaults review.
  const [scalingMax, setScalingMax] = useState(String(existingSettings?.scaling?.max ?? 100));
  // `buffer` is a legacy floor-padding/idle-threshold knob that was meaningful
  // before backfill existed. Backfill now provides headroom via a ratio
  // (more flexible) and the floor-padding effect is dominated by it. We
  // preserve any value already saved on the pool but no longer expose it in
  // the UI; new pools default to 0 (no padding) and operators who need to
  // unset a legacy non-zero value can do so via raw settings JSON.
  const [scalingBuffer] = useState(String(existingSettings?.scaling?.buffer ?? 0));
  // Orchestrator version
  const [orchestratorVersion, setOrchestratorVersion] = useState<string>(existingSettings?.orchestratorVersion ?? '');
  const [orchestratorReleases, setOrchestratorReleases] = useState<Array<{ version: string; releasedAt: string }>>([]);
  const { getToken } = useAuth();
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken({ template: 'default' });
        const res = await fetch(`${getApiBaseUrl()}/api/admin/orchestrator-releases`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json() as { releases: Array<{ version: string; releasedAt: string }> };
          setOrchestratorReleases(data.releases ?? []);
        }
      } catch { /* ignore */ }
    })();
  }, [getToken]);

  // Demand sources — derive from legacy strategy type or explicit config
  const derivedSources = (() => {
    if (existingSettings?.demandSources) return existingSettings.demandSources;
    const t = existingSettings?.strategy?.type;
    if (t === 'scheduled-strict') return { reactive: false, scheduled: true, predictive: false };
    if (t === 'scheduled' || t === 'scheduled-floor') return { reactive: true, scheduled: true, predictive: false };
    if (t === 'predictive') return { reactive: true, scheduled: true, predictive: true };
    return { reactive: true, scheduled: false, predictive: false };
  })();

  type DemandSourceType = 'reactive' | 'scheduled' | 'predictive' | 'backfill';

  const [enabledSources, setEnabledSources] = useState<Set<DemandSourceType>>(() => {
    const s = new Set<DemandSourceType>();
    if (derivedSources.reactive !== false) s.add('reactive');
    if (derivedSources.scheduled) s.add('scheduled');
    // Either flag enables the predictive section. Shadow vs. consume is a
    // sub-mode toggled within the section.
    if (derivedSources.predictive || (existingSettings?.demandSources as { predictiveShadow?: boolean })?.predictiveShadow) {
      s.add('predictive');
    }
    if (derivedSources.backfill !== false) s.add('backfill');
    return s;
  });

  // Predictive shadow mode (IL-137). Shadow runs the histogram pipeline but
  // contributes nothing to mediation — used for one-week validation before
  // flipping a real customer pool to active predictive.
  const [predictiveShadowMode, setPredictiveShadowMode] = useState<boolean>(
    (existingSettings?.demandSources as { predictiveShadow?: boolean })?.predictiveShadow === true,
  );

  const addSource = (source: DemandSourceType) => {
    setEnabledSources(prev => new Set([...prev, source]));
  };
  const removeSource = (source: DemandSourceType) => {
    setEnabledSources(prev => { const next = new Set(prev); next.delete(source); return next; });
  };

  // Restore demand source configuration to current platform defaults. Resets
  // enabled sources (reactive + backfill on, predictive off, scheduled
  // preserved since it depends on whether the user actually has schedule
  // entries) and clears all tuning knobs back to DEFAULT_STRATEGY_CONFIG
  // values. Does not save — user still needs to click Save to persist.
  const restoreDemandSourceDefaults = () => {
    setEnabledSources(prev => {
      const next = new Set<DemandSourceType>();
      next.add('reactive');
      next.add('backfill');
      if (prev.has('scheduled')) next.add('scheduled');
      return next;
    });
    setScaleUpThreshold('1');
    setScaleDownDelay('900');
    setCooldownSeconds('120');
    setEvaluationPeriods('1');
    setPreScaleMinutes('15');
    setLookaheadMinutes('4');
    setPredictiveQuantile('0.75');
    setPredictiveTimezone('');
    setPredictiveShadowMode(false);
    setBackfillThreshold('0.5');
    setBackfillMultiplier('1.5');
    setBackfillAdaptive(false);
    setBackfillAdaptiveWindow('5');
    setBackfillAdaptiveSaturation('1');
    // Reset observation radius to the platform default tier. We do not
    // clear any explicit radiusKm override here — that lives in the raw
    // settings JSON and is only touched if the operator cleared it
    // themselves; restoring "defaults" shouldn't wipe a deliberate override.
    setObservationRadiusTier('global');
  };

  // Per-source tuning knobs
  const [scaleUpThreshold, setScaleUpThreshold] = useState(String(existingSettings?.strategy?.scaleUpThreshold ?? 1));
  const [scaleDownDelay, setScaleDownDelay] = useState(String(existingSettings?.strategy?.scaleDownDelaySeconds ?? 900));
  const [cooldownSeconds, setCooldownSeconds] = useState(String(existingSettings?.strategy?.cooldownSeconds ?? 120));
  const [evaluationPeriods, setEvaluationPeriods] = useState(String(existingSettings?.strategy?.evaluationPeriods ?? 1));
  const [preScaleMinutes, setPreScaleMinutes] = useState(String(existingSettings?.strategy?.preScaleMinutes ?? 15));
  const [lookaheadMinutes, setLookaheadMinutes] = useState(String(existingSettings?.strategy?.lookaheadMinutes ?? 4));
  // Predictive histogram knobs (IL-136). Default quantile 0.75 from the IL-135
  // closed-loop sweep. Timezone is IANA; empty string means "inherit UTC".
  const [predictiveQuantile, setPredictiveQuantile] = useState(String(existingSettings?.predictiveQuantile ?? 0.75));
  const [predictiveTimezone, setPredictiveTimezone] = useState(String(existingSettings?.timezone ?? ''));
  const [backfillThreshold, setBackfillThreshold] = useState(String(existingSettings?.backfill?.threshold ?? 0.5));
  const [backfillMultiplier, setBackfillMultiplier] = useState(String(existingSettings?.backfill?.growthMultiplier ?? 1.5));
  const [backfillAdaptive, setBackfillAdaptive] = useState<boolean>(existingSettings?.backfill?.adaptive === true);
  const [backfillAdaptiveWindow, setBackfillAdaptiveWindow] = useState(String(existingSettings?.backfill?.adaptiveWindowMinutes ?? 5));
  const [backfillAdaptiveSaturation, setBackfillAdaptiveSaturation] = useState(String(existingSettings?.backfill?.adaptiveSaturationArrivalsPerMin ?? 1));

  // Reactive demand observation radius. Operators pick a friendly latency
  // tier; we store the tier name (not the km value) so the pool tracks the
  // marketplace's LATENCY_TIERS table if it ever changes. "global" is the
  // default and means "see all demand for our app:versions regardless of
  // geography" — the right answer for most pools.
  type RadiusTier = 'edge' | 'regional' | 'continental' | 'global';
  // Migrate the legacy tier names from older configs into the current set.
  const migrateRadiusTier = (raw: string | undefined): RadiusTier => {
    switch (raw) {
      case 'local':
        return 'edge';
      case 'intercontinental':
        return 'continental';
      case 'edge':
      case 'regional':
      case 'continental':
      case 'global':
        return raw;
      default:
        return 'global';
    }
  };
  const [observationRadiusTier, setObservationRadiusTier] = useState<RadiusTier>(
    migrateRadiusTier(existingSettings?.observation?.radiusTier as string | undefined),
  );
  // If the existing config uses an explicit radiusKm (the power-user
  // override), surface it read-only alongside the tier so operators see what
  // they're overriding. The tier dropdown is disabled in that case.
  const existingRadiusKmOverride = typeof existingSettings?.observation?.radiusKm === 'number'
    ? existingSettings.observation.radiusKm
    : null;

  const [capacitySchedule, setCapacitySchedule] = useState<CapacitySchedule | undefined>(() => {
    if (pool.capacityScheduleJson) {
      try { return JSON.parse(pool.capacityScheduleJson); } catch { /* ignore */ }
    }
    return undefined;
  });

  const handleSubmit = () => {
    if (!name.trim() || !region) return;

    const parseIntVal = (v: string) => { const n = parseInt(v, 10); return isNaN(n) ? undefined : n; };
    const parseNum = (v: string) => { const n = parseFloat(v); return isNaN(n) ? undefined : n; };

    const backends = infrastructure.backends.map(b => {
      const backend: Record<string, unknown> = {
        id: b.id,
        cloud: b.cloud,
        region: b.region,
        instanceType: b.instanceType,
        imageId: b.imageId,
        weight: b.weight,
      };
      if (b.maxCapacity != null) backend.maxCapacity = b.maxCapacity;
      if (b.maxConcurrent != null && b.maxConcurrent > 1) backend.maxConcurrent = b.maxConcurrent;
      if (b.simpleSlots === false) backend.simpleSlots = false;
      if (b.startupMode && b.startupMode !== 'standard') backend.startupMode = b.startupMode;
      if (b.launchProtocolVersion != null && b.launchProtocolVersion !== 1) backend.launchProtocolVersion = b.launchProtocolVersion;
      // v3 orchestrator-behavior knobs. Mirror EditProfileDialog's serializer
      // — the pool-edit page was previously dropping these on save, so a
      // toggle would appear set in the UI but never persist to the DO.
      // Only emit when truthy/set; the read path uses `...b` so absent
      // fields render as undefined which the UI's `?? false` handles.
      if (b.shareWorkspace) backend.shareWorkspace = true;
      if (b.ultraFastSlots) backend.ultraFastSlots = true;
      if (b.profilePool) backend.profilePool = true;
      if (b.noWarm) backend.noWarm = true;
      if (b.instantOn) backend.instantOn = true;
      if (b.precache) backend.precache = true;
      if (b.noJobBoard) backend.noJobBoard = true;
      if (b.uiIsolation) backend.uiIsolation = true;
      if (b.streamingTarzst) backend.streamingTarzst = true;
      if (b.logJsonPath) backend.logJsonPath = b.logJsonPath;
      if (b.logLevel) backend.logLevel = b.logLevel;
      if (b.otelEndpoint) backend.otelEndpoint = b.otelEndpoint;
      if (b.otelAuth) backend.otelAuth = b.otelAuth;
      // AWS
      if (b.awsSubnets) backend.awsSubnets = b.awsSubnets.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsSecurityGroups) backend.awsSecurityGroups = b.awsSecurityGroups.split(',').map(s => s.trim()).filter(Boolean);
      if (b.awsInstanceProfile) backend.awsInstanceProfile = b.awsInstanceProfile;
      // GCP
      if (b.gcpProjectId) backend.gcpProjectId = b.gcpProjectId;
      if (b.gcpSubnetwork) backend.gcpSubnetwork = b.gcpSubnetwork;
      if (b.gcpFirewallTags) backend.gcpFirewallTags = b.gcpFirewallTags.split(',').map(s => s.trim()).filter(Boolean);
      if (b.gcpServiceAccount) backend.gcpServiceAccount = b.gcpServiceAccount;
      // CoreWeave
      if (b.cwApiServer) backend.cwApiServer = b.cwApiServer;
      if (b.cwKubeToken) backend.cwKubeToken = b.cwKubeToken;
      if (b.cwNamespace) backend.cwNamespace = b.cwNamespace;
      if (b.cwGpuCount != null) backend.cwGpuCount = b.cwGpuCount;
      if (b.cwCpuCount != null) backend.cwCpuCount = b.cwCpuCount;
      if (b.cwMemoryGb != null) backend.cwMemoryGb = b.cwMemoryGb;
      if (b.cwRootDiskSize) backend.cwRootDiskSize = b.cwRootDiskSize;
      if (b.cwRootPvcLabel) {
        const labels: Record<string, string> = {};
        b.cwRootPvcLabel.split(',').forEach(pair => {
          const [k, v] = pair.split('=').map(s => s.trim());
          if (k && v) labels[k] = v;
        });
        if (Object.keys(labels).length > 0) backend.cwRootPvcLabel = labels;
      }
      if (b.cwAdditionalDiskSize) {
        backend.cwAdditionalDisks = [{ name: 'data', size: b.cwAdditionalDiskSize }];
      }
      if (b.cwVsUsername) backend.cwVsUsername = b.cwVsUsername;
      if (b.cwVsPassword) backend.cwVsPassword = b.cwVsPassword;
      return backend;
    });

    // Build strategy config (tuning knobs — no longer has a type selector)
    const strategy: Record<string, unknown> = { type: 'reactive' }; // backward compat
    if (cooldownSeconds !== '120') strategy.cooldownSeconds = parseIntVal(cooldownSeconds);
    if (evaluationPeriods !== '1') strategy.evaluationPeriods = parseIntVal(evaluationPeriods);
    if (enabledSources.has('reactive')) {
      if (scaleUpThreshold !== '1') strategy.scaleUpThreshold = parseIntVal(scaleUpThreshold);
      if (scaleDownDelay !== '900') strategy.scaleDownDelaySeconds = parseIntVal(scaleDownDelay);
    }
    if (enabledSources.has('scheduled')) {
      if (preScaleMinutes !== '15') strategy.preScaleMinutes = parseIntVal(preScaleMinutes);
      if (!enabledSources.has('reactive') && scaleDownDelay !== '900') {
        strategy.scaleDownDelaySeconds = parseIntVal(scaleDownDelay);
      }
    }
    if (enabledSources.has('predictive')) {
      // Forecast horizon — should match backend warm-up time. Default 4 min.
      if (lookaheadMinutes !== '4') strategy.lookaheadMinutes = parseIntVal(lookaheadMinutes);
    }

    // Predictive: shadow mode runs the pipeline but contributes nothing to
    // mediation. Predictive section enabled with shadow flag → demandSources
    // emits predictive=false + predictiveShadow=true. Section enabled
    // without shadow → predictive=true.
    const predictiveActive = enabledSources.has('predictive') && !predictiveShadowMode;
    const predictiveShadowOn = enabledSources.has('predictive') && predictiveShadowMode;
    const demandSources: Record<string, unknown> = {
      reactive: enabledSources.has('reactive'),
      scheduled: enabledSources.has('scheduled'),
      predictive: predictiveActive,
      backfill: enabledSources.has('backfill'),
    };
    if (predictiveShadowOn) demandSources.predictiveShadow = true;

    const backfill = enabledSources.has('backfill')
      ? {
          threshold: parseNum(backfillThreshold) ?? 0.5,
          ...(backfillMultiplier !== '1.5' ? { growthMultiplier: parseNum(backfillMultiplier) } : {}),
          ...(backfillAdaptive ? { adaptive: true } : {}),
          ...(backfillAdaptive && backfillAdaptiveWindow !== '5'
            ? { adaptiveWindowMinutes: parseIntVal(backfillAdaptiveWindow) }
            : {}),
          ...(backfillAdaptive && backfillAdaptiveSaturation !== '1'
            ? { adaptiveSaturationArrivalsPerMin: parseNum(backfillAdaptiveSaturation) }
            : {}),
        }
      : undefined;

    // Preserve any existing observation lat/lng or explicit radiusKm override
    // the operator set via raw settings — the UI only exposes the friendly
    // tier, but we don't want to wipe deliberate power-user overrides on save.
    const observation: Record<string, unknown> = {};
    if (existingSettings?.observation?.lat !== undefined) observation.lat = existingSettings.observation.lat;
    if (existingSettings?.observation?.lng !== undefined) observation.lng = existingSettings.observation.lng;
    if (existingRadiusKmOverride !== null) observation.radiusKm = existingRadiusKmOverride;
    // Always persist the tier when the operator has explicitly chosen one
    // (i.e. anything non-default) or when there's no existing override.
    // Omitting the tier silently falls back to the server default ("global"),
    // which is what we want for the new-pool-left-unchanged case.
    if (observationRadiusTier !== 'global' || existingSettings?.observation?.radiusTier) {
      observation.radiusTier = observationRadiusTier;
    }

    // Predictive top-level fields (IL-136). Only emitted when explicitly
    // set; absent means server-side default (P75 / UTC).
    const predictivePatch: Record<string, unknown> = {};
    if (enabledSources.has('predictive')) {
      const q = parseNum(predictiveQuantile);
      if (q !== undefined && q !== 0.75) predictivePatch.predictiveQuantile = q;
      const tz = predictiveTimezone.trim();
      if (tz) predictivePatch.timezone = tz;
    }

    const settingsJson = JSON.stringify({
      backends,
      allocationStrategy: infrastructure.allocationStrategy,
      scaling: {
        min: parseIntVal(scalingMin) ?? 0,
        max: parseIntVal(scalingMax) ?? 100,
        buffer: parseIntVal(scalingBuffer) ?? 0,
      },
      strategy,
      demandSources,
      ...(backfill ? { backfill } : {}),
      ...(Object.keys(observation).length > 0 ? { observation } : {}),
      ...predictivePatch,
      tenancy,
      ...(orchestratorVersion ? { orchestratorVersion } : {}),
    });

    const capacityScheduleJson = capacitySchedule ? JSON.stringify(capacitySchedule) : null;

    onSave(settingsJson, capacityScheduleJson, name.trim(), region, active);
  };

  const formLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--weight-semibold)' as any,
    textTransform: 'uppercase',
    letterSpacing: 'var(--tracking-wide)',
    color: 'var(--text-secondary)',
    marginBottom: 'var(--space-1)',
  };

  const formRowStyle: React.CSSProperties = {
    marginBottom: 'var(--space-3)',
  };

  const formSectionStyle: React.CSSProperties = {
    marginTop: 'var(--space-4)',
    paddingTop: 'var(--space-3)',
    borderTop: 'var(--border-width) solid var(--border)',
  };

  const formSectionTitleStyle: React.CSSProperties = {
    margin: '0 0 var(--space-3) 0',
    fontSize: 'var(--text-xs)',
    fontWeight: 'var(--weight-bold)' as any,
    textTransform: 'uppercase',
    letterSpacing: 'var(--tracking-wide)',
    color: 'var(--text-primary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--space-1) var(--space-2)',
    border: 'var(--border-width) solid var(--border)',
    background: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: 'var(--text-sm)',
    fontFamily: 'var(--font-sans)',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
  };

  return (
    <div style={{ padding: 'var(--space-3) var(--space-4)', border: 'var(--border-width) solid var(--border)' }}>
      <div style={formRowStyle}>
        <label style={formLabelStyle}>Pool Name</label>
        <input
          style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Western North America Pool"
          disabled={saving}
        />
      </div>

      <div style={formRowStyle}>
        <label style={formLabelStyle}>Super Region</label>
        <select
          style={selectStyle}
          value={region}
          onChange={e => setRegion(e.target.value)}
          disabled={saving}
        >
          {SUPER_REGION_OPTIONS.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>
      </div>

      <div style={formRowStyle}>
        <label style={formLabelStyle}>Tenancy</label>
        <select
          style={selectStyle}
          value={tenancy}
          onChange={e => setTenancy(e.target.value as 'single' | 'organization' | 'multi')}
          disabled={saving}
        >
          <option value="single">Single — workers must match pool project</option>
          <option value="organization">Organization — workers from any project in the org</option>
          <option value="multi">Multi — workers from any project accepted</option>
        </select>
        {pool.type === 'by_request' && tenancy === 'single' && (
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning, #b45309)', marginTop: 'var(--space-1)' }}>
            by_request pools default to multi-tenancy. Setting single restricts workers to the pool's own project.
          </div>
        )}
      </div>

      <div style={formRowStyle}>
        <label style={formLabelStyle}>
          <input
            type="checkbox"
            checked={active}
            onChange={e => setActive(e.target.checked)}
            disabled={saving}
          />
          {' '}Active
        </label>
      </div>

      <div style={formSectionStyle}>
        <h3 style={formSectionTitleStyle}>Foundational Scaling Clamps</h3>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Hard limits on session capacity. The mediated demand signal is clamped to these bounds regardless of demand source output.
        </div>
        <div style={formRowStyle}>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <div>
              <label style={formLabelStyle}>Min (floor)</label>
              <input style={{ ...inputStyle, width: 80 }} type="number" min="0" value={scalingMin} onChange={e => setScalingMin(e.target.value)} disabled={saving} />
            </div>
            <div>
              <label style={formLabelStyle}>Max (ceiling)</label>
              <input style={{ ...inputStyle, width: 80 }} type="number" min="0" value={scalingMax} onChange={e => setScalingMax(e.target.value)} disabled={saving} />
            </div>
          </div>
        </div>
        <div style={formRowStyle}>
          <label style={formLabelStyle}>Orchestrator Version</label>
          <select style={selectStyle} value={orchestratorVersion} onChange={e => setOrchestratorVersion(e.target.value)} disabled={saving}>
            <option value="">Latest</option>
            {orchestratorReleases.map(r => (
              <option key={r.version} value={r.version}>
                v{r.version} ({new Date(r.releasedAt).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={formSectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-1)' }}>
          <h3 style={{ ...formSectionTitleStyle, margin: 0 }}>Demand Sources</h3>
          <button
            type="button"
            onClick={restoreDemandSourceDefaults}
            disabled={saving}
            title="Reset demand sources and tuning knobs to current platform defaults. You still need to click Save to persist."
            style={{ padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}
          >
            Restore Defaults
          </button>
        </div>
        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
          Each source produces a demand signal (floor &amp; ceiling). Signals are mediated: floors are maximized, ceilings are minimized. Ceiling wins conflicts (cost protection).
        </div>

        {/* Common tuning knobs */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <div>
            <label style={formLabelStyle}>Cooldown (s)</label>
            <input style={{ ...inputStyle, width: 80 }} type="number" min="0" value={cooldownSeconds} onChange={e => setCooldownSeconds(e.target.value)} disabled={saving} />
          </div>
        </div>

        {/* Source cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {enabledSources.has('reactive') && (
            <div style={{ border: 'var(--border-width) solid var(--border)', padding: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)' }}>Reactive</h4>
                <button type="button" onClick={() => removeSource('reactive')} disabled={saving} style={{ padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--gray-7)', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}>Remove</button>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Observes marketplace demand. Floor = current demand, ceiling = unlimited.
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <div>
                  <label style={formLabelStyle}>Scale-Up Threshold</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={scaleUpThreshold} onChange={e => setScaleUpThreshold(e.target.value)} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Scale-Down Delay (s)</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="0" value={scaleDownDelay} onChange={e => setScaleDownDelay(e.target.value)} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Eval Periods</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={evaluationPeriods} onChange={e => setEvaluationPeriods(e.target.value)} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Observation Radius</label>
                  <select
                    style={{ ...selectStyle, width: 200 }}
                    value={observationRadiusTier}
                    onChange={e => setObservationRadiusTier(e.target.value as RadiusTier)}
                    disabled={saving || existingRadiusKmOverride !== null}
                    title={existingRadiusKmOverride !== null
                      ? `Overridden by explicit radiusKm=${existingRadiusKmOverride} in raw settings`
                      : 'Geographic radius for observing marketplace demand. Matches the marketplace latency tier.'}
                  >
                    <option value="edge">Edge — ~25ms RTT (800km)</option>
                    <option value="regional">Regional — ~60ms RTT (2,400km)</option>
                    <option value="continental">Continental — ~120ms RTT (8,000km)</option>
                    <option value="global">Global — ~250ms RTT (20,015km)</option>
                  </select>
                  {existingRadiusKmOverride !== null && (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warning, #b45309)', marginTop: 'var(--space-1)' }}>
                      Overridden by explicit radiusKm={existingRadiusKmOverride} in raw settings. Clear it there to re-enable the tier selector.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {enabledSources.has('scheduled') && (
            <div style={{ border: 'var(--border-width) solid var(--border)', padding: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)' }}>Scheduled</h4>
                <button type="button" onClick={() => removeSource('scheduled')} disabled={saving} style={{ padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--gray-7)', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}>Remove</button>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Hand-crafted demand from capacity spans. Floor and ceiling are additive across overlapping spans.
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <div>
                  <label style={formLabelStyle}>Pre-Scale Minutes</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="0" value={preScaleMinutes} onChange={e => setPreScaleMinutes(e.target.value)} disabled={saving} />
                </div>
              </div>
            </div>
          )}

          {enabledSources.has('predictive') && (
            <div style={{ border: 'var(--border-width) solid var(--border)', padding: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)' }}>Predictive</h4>
                <button type="button" onClick={() => removeSource('predictive')} disabled={saving} style={{ padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--gray-7)', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}>Remove</button>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Forecasts demand from a weekly adaptive histogram of past observations.
                Returns the smoothed quantile at <code>now + lookahead</code> as a floor —
                competes with reactive/scheduled via max(). Cold-starts as floor=0 until
                ≥ 4 weeks of history accrue.
              </div>
              <label style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'center', fontSize: 'var(--text-xs)', marginBottom: 'var(--space-2)' }}>
                <input type="checkbox" checked={predictiveShadowMode} onChange={e => setPredictiveShadowMode(e.target.checked)} disabled={saving} />
                <span>
                  <strong>Shadow mode</strong> — run the pipeline and log forecasts to <code>pool_demand_predictions</code>,
                  but contribute floor=0 to mediation. Use for ≥ 1 week of validation before flipping to active.
                </span>
              </label>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                <div>
                  <label style={formLabelStyle}>Lookahead (min)</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" value={lookaheadMinutes} onChange={e => setLookaheadMinutes(e.target.value)} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Forecast Quantile</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="0.5" max="0.99" step="0.05" value={predictiveQuantile} onChange={e => setPredictiveQuantile(e.target.value)} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Timezone (IANA)</label>
                  <input style={{ ...inputStyle, width: 200 }} type="text" placeholder="UTC" value={predictiveTimezone} onChange={e => setPredictiveTimezone(e.target.value)} disabled={saving} />
                </div>
              </div>
              <PredictiveObservability
                predictions={predictions}
                bins={histogramBins}
                quarantine={quarantine}
                tz={predictiveTimezoneServed}
              />
            </div>
          )}
          {enabledSources.has('backfill') && (
            <div style={{ border: 'var(--border-width) solid var(--border)', padding: 'var(--space-3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                <h4 style={{ margin: 0, fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)' }}>Backfill</h4>
                <button type="button" onClick={() => removeSource('backfill')} disabled={saving} style={{ padding: 'var(--space-1) var(--space-3)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--gray-7)', border: 'var(--border-width) solid var(--border)', cursor: 'pointer' }}>Remove</button>
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                Raises demand when available session slots drop below a percentage threshold. Ensures headroom for incoming sessions.
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <div>
                  <label style={formLabelStyle}>Threshold (%)</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="0" max="100" step="5" value={Math.round(parseFloat(backfillThreshold) * 100)} onChange={e => setBackfillThreshold(String((parseInt(e.target.value) || 50) / 100))} disabled={saving} />
                </div>
                <div>
                  <label style={formLabelStyle}>Growth Multiplier</label>
                  <input style={{ ...inputStyle, width: 80 }} type="number" min="1" max="5" step="0.1" value={backfillMultiplier} onChange={e => setBackfillMultiplier(e.target.value)} disabled={saving} />
                </div>
              </div>
              <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: 'var(--border-width) dashed var(--border)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: saving ? 'default' : 'pointer', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-semibold)' }}>
                  <input type="checkbox" checked={backfillAdaptive} onChange={e => setBackfillAdaptive(e.target.checked)} disabled={saving} />
                  Adaptive multiplier
                </label>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)' }}>
                  Scales the growth multiplier by observed session arrival velocity. Idle pools collapse to 1.0× (restore threshold only); pools seeing ≥ saturation arrivals/min get the full multiplier.
                </div>
                {backfillAdaptive && (
                  <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                    <div>
                      <label style={formLabelStyle}>Window (min)</label>
                      <input style={{ ...inputStyle, width: 80 }} type="number" min="1" max="60" step="1" value={backfillAdaptiveWindow} onChange={e => setBackfillAdaptiveWindow(e.target.value)} disabled={saving} />
                    </div>
                    <div>
                      <label style={formLabelStyle}>Saturation (arr/min)</label>
                      <input style={{ ...inputStyle, width: 80 }} type="number" min="0.1" max="100" step="0.1" value={backfillAdaptiveSaturation} onChange={e => setBackfillAdaptiveSaturation(e.target.value)} disabled={saving} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Add source buttons */}
        {(enabledSources.size < 4) && (
          <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
            {!enabledSources.has('reactive') && (
              <button type="button" onClick={() => addSource('reactive')} disabled={saving} style={{ padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'var(--border-width) dashed var(--border)', cursor: 'pointer' }}>+ Reactive</button>
            )}
            {!enabledSources.has('scheduled') && (
              <button type="button" onClick={() => addSource('scheduled')} disabled={saving} style={{ padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'var(--border-width) dashed var(--border)', cursor: 'pointer' }}>+ Scheduled</button>
            )}
            {!enabledSources.has('predictive') && (
              <button type="button" onClick={() => addSource('predictive')} disabled={saving} style={{ padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'var(--border-width) dashed var(--border)', cursor: 'pointer' }}>+ Predictive</button>
            )}
            {!enabledSources.has('backfill') && (
              <button type="button" onClick={() => addSource('backfill')} disabled={saving} style={{ padding: 'var(--space-2) var(--space-4)', fontSize: 'var(--text-xs)', fontWeight: 'var(--weight-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-wide)', background: 'var(--bg-primary)', color: 'var(--text-primary)', border: 'var(--border-width) dashed var(--border)', cursor: 'pointer' }}>+ Backfill</button>
            )}
          </div>
        )}

        {enabledSources.size === 0 && (
          <div style={{ padding: 'var(--space-4)', textAlign: 'center', border: 'var(--border-width) solid var(--border)', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
            No demand sources configured. Add at least one source to enable scaling.
          </div>
        )}
      </div>

      <div style={formSectionStyle}>
        <h3 style={formSectionTitleStyle}>Infrastructure</h3>
        <InfrastructureEditor
          value={infrastructure}
          onChange={setInfrastructure}
          disabled={saving}
          infraDefaults={infraDefaults}
        />
      </div>

      <div style={formSectionStyle}>
        <h3 style={formSectionTitleStyle}>Capacity Schedule</h3>
        <CapacityScheduleEditor
          value={capacitySchedule}
          onChange={setCapacitySchedule}
          allowMediated={true}
          disabled={saving}
        />
      </div>

      {message && (
        <div style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-xs)',
          border: 'var(--border-width) solid',
          borderColor: message.ok ? 'var(--text-primary)' : 'var(--color-error)',
          color: message.ok ? 'var(--text-primary)' : 'var(--color-error)',
        }}>
          {message.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button
          className={styles.applyButton}
          onClick={handleSubmit}
          disabled={saving || !name.trim()}
        >
          {saving ? 'Saving...' : 'Update Pool'}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Instances & Orchestrators (unified view)
// ---------------------------------------------------------------------------

function InstancesAndOrchestrators({
  instances,
  orchestrators,
  actionInProgress,
  onDrain,
  onTerminate,
  onPin,
  onReboot,
  onTerminateInstance,
  onRefresh,
  onRefreshDesiredState,
  refreshDesiredStateLoading,
  refreshDesiredStateData,
}: {
  instances: EnrichedInstance[];
  orchestrators: OrchestratorInfo[];
  actionInProgress: string | null;
  onDrain: (id: string) => void;
  onTerminate: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onReboot: (instanceId: string) => void;
  onTerminateInstance: (instanceId: string) => void;
  onRefresh: () => void;
  onRefreshDesiredState: () => void;
  refreshDesiredStateLoading: boolean;
  refreshDesiredStateData: unknown;
}) {
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      onRefresh();
    } finally {
      // Brief visual feedback then reset
      setTimeout(() => setRefreshing(false), 600);
    }
  };
  // Build a map of instanceId → orchestrator for joining
  const orchByInstanceId = new Map<string, OrchestratorInfo>();
  const orchByWorkerId = new Map<string, OrchestratorInfo>();
  for (const o of orchestrators) {
    orchByWorkerId.set(o.workerId, o);
    if (o.instanceId) {
      orchByInstanceId.set(o.instanceId, o);
    }
  }

  // Build unified rows:
  // 1. Cloud instances (joined with orchestrators when connected)
  // 2. Unmanaged orchestrators (no cloud instance)
  const connectedOrchestratorIds = new Set<string>();
  for (const inst of instances) {
    if (inst.orchestratorId) {
      connectedOrchestratorIds.add(inst.orchestratorId);
    }
  }
  const unmanagedOrchestrators = orchestrators.filter(
    o => !connectedOrchestratorIds.has(o.workerId) && !o.managed
  );
  const managedOrphanOrchestrators = orchestrators.filter(
    o => !connectedOrchestratorIds.has(o.workerId) && o.managed
  );

  const totalCount = instances.length + unmanagedOrchestrators.length + managedOrphanOrchestrators.length;

  // Inline status for the last refresh-desired-state action, surfaced next
  // to the button so operators can see at a glance whether the broadcast
  // landed without scrolling away from the table they were diagnosing.
  const refreshStatus = (() => {
    if (refreshDesiredStateLoading) return null;
    const d = refreshDesiredStateData as
      | { ok?: boolean; sockets?: number; configRevision?: number; error?: string }
      | null;
    if (!d) return null;
    if (d.ok) {
      return {
        text: `Broadcast → ${d.sockets ?? 0} sockets · rev ${d.configRevision ?? 0}`,
        color: 'var(--color-success, #16a34a)',
      };
    }
    return {
      text: d.error ? `Failed: ${d.error}` : 'Broadcast failed',
      color: 'var(--color-error, #dc2626)',
    };
  })();

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          Machines & Orchestrators ({totalCount})
        </h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {refreshStatus && (
            <span style={{ fontSize: 'var(--text-xs)', color: refreshStatus.color }}>
              {refreshStatus.text}
            </span>
          )}
          <button
            className={styles.actionButton}
            onClick={onRefreshDesiredState}
            disabled={refreshDesiredStateLoading}
            title="Re-broadcast PoolDO's cached desired-state with a bumped revision. Wakes up orcs stuck in RECONCILING (typically APPS=0 with active heartbeats — post-wipe steady state). Cheaper than rematerialize+push: skips the resolver round-trip. Use this first when workers are wedged; only escalate to rematerialize if the cached config itself is suspect."
            style={{ borderColor: 'var(--color-warning, #d97706)', color: 'var(--color-warning, #d97706)' }}
          >
            {refreshDesiredStateLoading ? 'Refreshing…' : 'Refresh Desired State'}
          </button>
          <span className={styles.refreshIndicator}>Auto-refresh 10s</span>
          <button
            className={styles.actionButton}
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ opacity: refreshing ? 0.5 : 1 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className={styles.empty}>No machines or orchestrators</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Machine ID</th>
              <th>Backend</th>
              <th>Machine Status</th>
              <th>Orchestrator</th>
              <th>State</th>
              <th>Region</th>
              <th>Sessions</th>
              <th>Apps</th>
              <th>Last Ping</th>
              <th>Flags</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {/* Cloud instances (with or without connected orchestrators) */}
            {instances.map(inst => {
              const orch = inst.orchestratorId
                ? orchByWorkerId.get(inst.orchestratorId) ?? null
                : null;
              return (
                <tr key={`inst-${inst.instanceId}`}>
                  <td>
                    <div>{inst.instanceId}</div>
                    {inst.publicIp && (
                      <a href={`vnc://${inst.publicIp}`} style={{ fontSize: 'var(--text-xs)', opacity: 0.6, color: 'inherit' }}>{inst.publicIp}</a>
                    )}
                  </td>
                  <td>{inst.backendId}</td>
                  <td>
                    <span className={provisioningBadgeClass(
                      inst.status === 'RUNNING' || inst.status === 'InService' ? 'provisioned' :
                      inst.status === 'TERMINATED' || inst.status === 'Terminating' ? 'failed' : 'pending'
                    )}>
                      {inst.status}
                    </span>
                  </td>
                  <td>{orch ? orch.workerId : <span style={{ color: 'var(--text-tertiary)' }}>not connected</span>}</td>
                  <td>
                    {orch ? (
                      <span className={orchestratorStateBadgeClass(orch.state)}>{orch.state}</span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)' }}>-</span>
                    )}
                  </td>
                  <td>{orch ? `${orch.region}${orch.zone ? `/${orch.zone}` : ''}` : '-'}</td>
                  <td>{orch ? `${orch.activeSessions}/${orch.maxConcurrentSessions}` : '-'}</td>
                  <td>{orch ? orch.provisionedApps.length : '-'}</td>
                  <td>{orch ? <span title={orch.lastPingAt}>{relativeTime(orch.lastPingAt)}</span> : '-'}</td>
                  <td>
                    {orch && (
                      <>
                        {orch.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                        <span className={styles.managedIcon} title="Managed orchestrator">MGD</span>
                      </>
                    )}
                  </td>
                  <td>
                    <div className={styles.workerActions} style={{ gap: 'var(--space-1)' }}>
                      <button
                        className={styles.actionButton}
                        onClick={() => onReboot(inst.instanceId)}
                        disabled={!!actionInProgress}
                        title="Reboot cloud instance"
                        style={{ padding: '2px 6px', fontSize: 'var(--text-xs)', minWidth: 0 }}
                      >
                        R
                      </button>
                      <button
                        className={styles.dangerButton}
                        onClick={() => onTerminateInstance(inst.instanceId)}
                        disabled={!!actionInProgress}
                        title="Terminate cloud instance (pool will replace to keep desired capacity)"
                        style={{ padding: '2px 6px', fontSize: 'var(--text-xs)', minWidth: 0 }}
                      >
                        X
                      </button>
                      {orch && (
                        <>
                          {orch.state !== 'draining' && (
                            <button
                              className={styles.actionButton}
                              onClick={() => onDrain(orch.workerId)}
                              disabled={!!actionInProgress}
                              title="Drain orchestrator"
                              style={{ padding: '2px 6px', fontSize: 'var(--text-xs)', minWidth: 0 }}
                            >
                              D
                            </button>
                          )}
                          <button
                            className={styles.dangerButton}
                            onClick={() => onTerminate(orch.workerId)}
                            disabled={!!actionInProgress}
                            title="Terminate orchestrator"
                            style={{ padding: '2px 6px', fontSize: 'var(--text-xs)', minWidth: 0 }}
                          >
                            K
                          </button>
                          <button
                            className={styles.actionButton}
                            onClick={() => onPin(orch.workerId, !orch.pinned)}
                            disabled={!!actionInProgress}
                            title={orch.pinned ? 'Unpin' : 'Pin'}
                            style={{ padding: '2px 6px', fontSize: 'var(--text-xs)', minWidth: 0 }}
                          >
                            P
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {/* Managed orchestrators connected but not in instance list (orphans) */}
            {managedOrphanOrchestrators.map(w => (
              <tr key={`orch-${w.workerId}`}>
                <td style={{ color: 'var(--text-tertiary)' }}>{w.instanceId || 'unknown'}</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td>{w.workerId}</td>
                <td><span className={orchestratorStateBadgeClass(w.state)}>{w.state}</span></td>
                <td>{w.region}{w.zone ? `/${w.zone}` : ''}</td>
                <td>{w.activeSessions}/{w.maxConcurrentSessions}</td>
                <td>{w.provisionedApps.length}</td>
                <td title={w.lastPingAt}>{relativeTime(w.lastPingAt)}</td>
                <td>
                  {w.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                  <span className={styles.managedIcon} title="Managed orchestrator">MGD</span>
                </td>
                <td>
                  <div className={styles.workerActions}>
                    {w.state !== 'draining' && (
                      <button className={styles.actionButton} onClick={() => onDrain(w.workerId)} disabled={!!actionInProgress} title="Drain">Drain</button>
                    )}
                    <button className={styles.dangerButton} onClick={() => onTerminate(w.workerId)} disabled={!!actionInProgress} title="Terminate">Kill</button>
                    <button className={styles.actionButton} onClick={() => onPin(w.workerId, !w.pinned)} disabled={!!actionInProgress} title={w.pinned ? 'Unpin' : 'Pin'}>{w.pinned ? 'Unpin' : 'Pin'}</button>
                  </div>
                </td>
              </tr>
            ))}

            {/* Unmanaged (external) orchestrators */}
            {unmanagedOrchestrators.map(w => (
              <tr key={`ext-${w.workerId}`}>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td>{w.workerId}</td>
                <td><span className={orchestratorStateBadgeClass(w.state)}>{w.state}</span></td>
                <td>{w.region}{w.zone ? `/${w.zone}` : ''}</td>
                <td>{w.activeSessions}/{w.maxConcurrentSessions}</td>
                <td>{w.provisionedApps.length}</td>
                <td title={w.lastPingAt}>{relativeTime(w.lastPingAt)}</td>
                <td>
                  <span className={styles.unmanagedIcon} title="Unmanaged — not provisioned by pool infrastructure">EXT</span>
                  {w.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                </td>
                <td>
                  <div className={styles.workerActions}>
                    {w.state !== 'draining' && (
                      <button className={styles.actionButton} onClick={() => onDrain(w.workerId)} disabled={!!actionInProgress} title="Drain">Drain</button>
                    )}
                    <button className={styles.dangerButton} onClick={() => onTerminate(w.workerId)} disabled={!!actionInProgress} title="Terminate">Kill</button>
                    <button className={styles.actionButton} onClick={() => onPin(w.workerId, !w.pinned)} disabled={!!actionInProgress} title={w.pinned ? 'Unpin' : 'Pin'}>{w.pinned ? 'Unpin' : 'Pin'}</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Badge helpers
// ---------------------------------------------------------------------------

function orchestratorStateBadgeClass(state: string): string {
  switch (state) {
    case 'available': return styles.stateAvailable;
    case 'busy': return styles.stateBusy;
    case 'reconciling': return styles.stateProvisioning;
    case 'draining': return styles.stateDraining;
    case 'registered': return styles.stateRegistered;
    default: return styles.badge;
  }
}

function provisioningBadgeClass(status: string): string {
  if (status === 'provisioned') return styles.provProvisioned;
  if (status === 'failed') return styles.provFailed;
  return styles.provPending;
}
